import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './orderdesk.css';
import { ReportArea, ReportFlag, ReportUpdates, ReportsView } from './DeskReports';
import FlowReview from './FlowReview';

// Order Desk: queues (left) → orders / batches in the queue (middle) → full detail with the next
// step (right). Phase 1: live data and notes; actions open the existing pages.
const TEAM_LABEL = { office: 'Office', warehouse: 'Warehouse', all: 'Everyone' };
const STEP_INDEX = { IMPORTED: 1, ON_HOLD: 1, ALLOCATING: 1, PRODUCTION: 2, READY_TO_PACK: 3, PACKED: 4, AWAITING_SHIPMENT_WEBHOOK: 4, IN_TRANSIT: 4, DELIVERED: 5, CLOSED: 6, RTO_IN_PROGRESS: 4, RTO_RECEIVED: 4, RETURN_RECEIVED: 5 };
const LINE_STATUS = {
  PENDING_ALLOCATION: ['Not allocated', 'warn'], RESERVED_FROM_STOCK: ['From stock', 'good'], IN_BATCH: ['In batch', 'acc'], RECEIVED: ['Ready', 'good'],
  PACKED: ['Packed', 'good'], SHIPPED: ['Shipped', 'plain'], DELIVERED: ['Delivered', 'plain'], RTO_RETURNED: ['Returned', 'bad'], CUSTOMER_RETURNED: ['Returned', 'bad'],
  RESTOCK_PENDING: ['To restock', 'warn'], RESTOCKED: ['Restocked', 'plain'], CANCELLED: ['Cancelled', 'plain'],
};
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const dt = (ms, withTime) => (ms ? new Date(ms).toLocaleString('en-IN', { day: 'numeric', month: 'short', ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}), timeZone: 'Asia/Kolkata' }) : '—');
const dIso = (iso) => (iso ? new Date(`${iso}T00:00:00+05:30`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');
const CH = { whatsapp: 'WhatsApp', instagram: 'Instagram', messenger: 'Messenger', email: 'Email', contact_form: 'Form' };
// system codes in plain words
const words = (v) => String(v || '').toLowerCase().replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const titleCase = (v) => String(v || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const ACTION_WORD = {
  MARK_USED_ELIGIBLE: 'Can be marked used from stock', PACK_ELIGIBLE: 'Ready to pack', RESTOCK_PENDING: 'Returned fabric to restock',
  EXCESS_RESTOCK_PENDING: 'Extra fabric to restock', UNDO_RESTOCK_ELIGIBLE: 'Restock can be undone',
  INVOICE_REVIEW_REQUIRED: 'Invoice needs review', PAYMENT_RECONCILE_HOLD: 'Payment on hold',
};

function Tag({ tone = 'plain', children }) { return <span className={`od-tag od-${tone}`}>{children}</span>; }

function Links({ links }) {
  return <div className="od-links">{links.map((l) => <a key={l.href + l.label} className="od-link" href={l.href}>{l.label}</a>)}</div>;
}


const V_LABEL = {
  pending: ['Needs a check', 'warn'], waiting_customer: ['Waiting for the customer to confirm on WhatsApp', 'acc'], call_pending: ['Call task: confirm with the customer', 'acc'],
  cancel_requested: ['Cancellation requested', 'bad'], verified: ['Verified', 'good'], auto_verified: ['Auto-verified: no flags', 'good'],
};
const SEV = { serious: 'bad', check: 'warn', info: 'plain' };

// Verification (phase 2): what the risk check found and what the office can do next
function VerifyPanel({ api, o, onDone, toast }) {
  const v = o.verification || {};
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null);
  const status = v.status || (o.verifyBlock ? 'pending' : null);
  if (!status) return null;
  const blocked = ['pending', 'waiting_customer', 'call_pending'].includes(status);
  const serious = (v.flags || []).some((f) => f.severity === 'serious');
  const dup = (v.flags || []).find((f) => f.code === 'duplicate');
  const isCod = o.payment.mode === 'COD';
  const run = async (key, path, body, okText) => {
    setBusy(key);
    try {
      await api(path, 'POST', body);
      toast(okText);
      setReason('');
      onDone();
    } catch (e) { toast(e.message, true); } finally { setBusy(null); }
  };
  const verifyPath = `/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/verify`;
  const [label, tone] = V_LABEL[status] || [status, 'plain'];
  const who = v.by ? ` · ${v.by}` : '';
  const how = { manual: 'checked by team', call: 'confirmed on call', whatsapp: 'customer confirmed on WhatsApp', auto: 'automatic' }[v.method] || '';
  return (
    <section className={`od-verify ${tone}`} aria-label="Verification">
      <div className="od-verify-h">
        <b>{label}</b>
        <span className="od-small">{v.atMs ? `${dt(v.atMs, true)}${how ? ` · ${how}` : ''}${who}` : v.checkedAtMs ? `checked ${dt(v.checkedAtMs, true)}` : 'risk check running'}</span>
      </div>
      {v.reason && <p className="od-small">Reason: {v.reason}</p>}
      {v.cancelReason && <p className="od-small">Cancel reason: {v.cancelReason}</p>}
      {(v.flags || []).length > 0 && (
        <ul className="od-checks">
          {v.flags.map((f) => <li key={f.code + f.label} className={SEV[f.severity]}><Tag tone={SEV[f.severity]}>{f.severity === 'serious' ? 'Serious' : f.severity === 'check' ? 'Check' : 'Info'}</Tag><span><b>{f.label}</b>{f.detail ? ` · ${f.detail}` : ''}</span></li>)}
        </ul>
      )}
      {(v.passed || []).length > 0 && <p className="od-small od-passed">✓ {v.passed.join(' · ')}</p>}

      {blocked && (
        <>
          <label className="od-label" htmlFor={`od-reason-${o.id}`}>{serious ? 'Reason (required for serious flags)' : 'Note (optional)'}</label>
          <input id={`od-reason-${o.id}`} className="od-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={serious ? 'e.g. Called, customer confirmed both orders' : 'e.g. Regular customer'} />
          {status === 'call_pending' ? (
            <div className="od-actions">
              <button className="od-btn od-pri" disabled={!!busy} onClick={() => run('c1', verifyPath, { action: 'call_outcome', outcome: 'confirmed', reason }, 'Verified: customer confirmed')}>Customer confirmed</button>
              <button className="od-btn" disabled={!!busy} onClick={() => run('c2', verifyPath, { action: 'call_outcome', outcome: 'no_answer', reason }, 'Noted: no answer')}>No answer{v.callTask?.noAnswer ? ` (${v.callTask.noAnswer})` : ''}</button>
              <button className="od-btn" disabled={!!busy} onClick={() => run('c3', verifyPath, { action: 'call_outcome', outcome: 'cancel', reason }, 'Cancellation requested')}>Wants to cancel</button>
              {v.callTask?.phone && <span className="od-small od-mono">Call {v.callTask.phone}</span>}
            </div>
          ) : (
            <div className="od-actions">
              <button className="od-btn od-pri" disabled={!!busy || (serious && reason.trim().length < 5)} onClick={() => run('v', verifyPath, { action: 'verify', reason }, 'Verified: order goes to the warehouse / batch')}>Verify · send to warehouse</button>
              {isCod && status !== 'waiting_customer' && <button className="od-btn" disabled={!!busy} onClick={() => run('w', `/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/whatsapp-cod`, {}, 'WhatsApp sent: waiting for the customer')}>WhatsApp to confirm COD</button>}
              <button className="od-btn" disabled={!!busy} onClick={() => run('t', verifyPath, { action: 'call' }, 'Call task created')}>Call task</button>
              {dup && <button className="od-btn" disabled={!!busy || reason.trim().length < 5} onClick={() => run('k', verifyPath, { action: 'keep_both', reason }, 'Verified: not a duplicate')}>Not a duplicate · keep both</button>}
              <button className="od-btn od-danger" disabled={!!busy || reason.trim().length < 3} onClick={() => run('x', verifyPath, { action: 'cancel_request', reason }, 'Cancellation requested')}>{dup ? 'Duplicate · cancel this order' : 'Request cancellation'}</button>
            </div>
          )}
          {dup && <div className="od-small">Other order{dup.others?.length > 1 ? 's' : ''}: {(dup.others || []).map((x) => <button key={x.orderId + x.sku} className="od-link" onClick={() => onDone(x.orderId)}>#{x.number} ({x.sku})</button>)}</div>}
          {serious && reason.trim().length < 5 && <p className="od-small">Write a short reason to verify an order with a serious flag.</p>}
        </>
      )}
      {status === 'cancel_requested' && (
        <div className="od-actions">
          {o.shopifyAdminUrl ? <a className="od-btn od-danger" href={o.shopifyAdminUrl} target="_blank" rel="noreferrer">Cancel in Shopify</a> : <span className="od-small">Custom order: cancel it in Zoho.</span>}
          <span className="od-small">Production is blocked. Once cancelled in Shopify, the order is cancelled here automatically.</span>
        </div>
      )}
      {['verified', 'auto_verified'].includes(status) && ['IMPORTED', 'ALLOCATING', 'ON_HOLD'].includes(o.status) && (
        <div className="od-actions">
          <button className="od-btn" disabled={!!busy} onClick={() => run('u', verifyPath, { action: 'undo' }, 'Verification undone: back in the Verify queue')}>Undo verification</button>
          <button className="od-btn" disabled={!!busy} onClick={() => run('r', verifyPath, { action: 'recheck' }, 'Checked again')}>Run the check again</button>
        </div>
      )}
    </section>
  );
}

// ---- Warehouse actions (phase 4): the same server steps as Stock House / Orders / Production pages
const COURIERS = [
  { id: 'delhivery', label: 'Delhivery', check: (a) => /^\d{12,16}$/.test(a), hint: '12–16 digits' },
  { id: 'shiprocket', label: 'Shiprocket', check: (a) => /^[A-Z0-9]{8,20}$/i.test(a), hint: '8–20 letters/digits' },
  { id: 'professional couriers', label: 'Professional Couriers', check: (a) => /^[A-Z]{2,4}\d{6,}$/i.test(a), hint: 'like SRT40480345' },
  { id: 'trackon', label: 'Trackon', check: (a) => /^\d{9,14}$/.test(a), hint: '9–14 digits' },
  { id: 'self', label: 'Hand delivery / pickup', check: () => true, hint: 'no AWB needed' },
  { id: 'other', label: 'Other courier', check: (a) => /^[A-Z0-9-]{6,25}$/i.test(a), hint: '6–25 letters/digits' },
];

function PackPanel({ api, o, toast, onDone }) {
  const [courier, setCourier] = useState(() => (/^delhivery$/i.test(o.shipment.courier || '') ? 'delhivery' : o.shipment.courier ? 'other' : 'delhivery'));
  const [awb, setAwb] = useState(o.shipment.awb || '');
  const [busy, setBusy] = useState(false);
  const c = COURIERS.find((x) => x.id === courier);
  const value = courier === 'self' ? `SELF-${o.number || o.id}` : awb.trim().replace(/\s+/g, '');
  const ok = courier === 'self' || (value && c.check(value));
  const lines = o.items.filter((i) => i.status !== 'CANCELLED' && i.metres > 0).map((i) => ({ sku: i.sku, qty: i.metres }));
  const pack = async () => {
    setBusy(true);
    try {
      await api(`/api/v9/orders/${encodeURIComponent(o.id)}/pack`, 'POST', { shipmentPartner: courier, awb: value, packages: [{ packageRef: `PKG-${Date.now()}`, lineItems: lines }] });
      toast(`Packed #${o.number}${courier === 'self' ? ' (hand delivery)' : ` · ${c.label} ${value}`}`);
      onDone();
    } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  if (!lines.length) {
    return (
      <section className="od-verify warn" aria-label="Pack">
        <div className="od-verify-h"><b>Nothing left to pack</b></div>
        <p className="od-small">Every fabric on this order was cancelled. Restock any fabric that came in; the order closes once it is back on the shelf.</p>
      </section>
    );
  }
  return (
    <section className="od-verify acc" aria-label="Pack">
      <div className="od-verify-h"><b>Pack this order</b><span className="od-small">{lines.length} fabric{lines.length === 1 ? '' : 's'} · {Math.round(lines.reduce((t, l) => t + l.qty, 0) * 10) / 10} m</span></div>
      <label className="od-label" htmlFor={`od-courier-${o.id}`}>Courier</label>
      <select id={`od-courier-${o.id}`} className="od-input" value={courier} onChange={(e) => setCourier(e.target.value)}>{COURIERS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</select>
      {courier !== 'self' && (
        <>
          <label className="od-label" htmlFor={`od-awb-${o.id}`}>AWB / tracking number (scan or type)</label>
          <input id={`od-awb-${o.id}`} className="od-input od-big" inputMode="text" autoComplete="off" value={awb} onChange={(e) => setAwb(e.target.value)} placeholder={c.hint} />
          {value && !ok && <p className="od-small od-late">That doesn't look like a {c.label} number ({c.hint}).</p>}
        </>
      )}
      <button className="od-btn od-pri od-big" disabled={busy || !ok} onClick={pack}>{busy ? 'Packing…' : 'Mark packed'}</button>
    </section>
  );
}

// Receive one fabric of a batch. Each order is offered only what it still needs from this batch (its metres minus
// what earlier deliveries gave it), so a repeat or partial delivery can't over-fill an order. Typing the metres that
// arrived fills the orders oldest first (cancelled orders last: received, then restocked); each amount can be changed,
// and whatever is left over goes to stock. The server caps it again (v9 receive safety net).
const r2 = (n) => Math.round(n * 100) / 100;
function receivePlan(open, got) {
  let left = got;
  const plan = {};
  [...open.filter((x) => !x.cancelled), ...open.filter((x) => x.cancelled)].forEach((x) => {
    const give = r2(Math.max(0, Math.min(x.need, left)));
    plan[x.lineItemId] = give ? String(give) : '';
    left = r2(left - give);
  });
  return plan;
}
function ReceiveItem({ api, batchId, item, toast, onDone, expected = null }) {
  const open = item.orders.filter((x) => x.lineItemId && x.allocated < x.metres - 1e-6).map((x) => ({ ...x, need: r2(x.metres - x.allocated) }));
  // prefilled with what the production house said it sent; the warehouse changes it if the count differs
  const [metres, setMetres] = useState(() => (expected ? String(expected) : ''));
  const [amounts, setAmounts] = useState(() => (expected ? receivePlan(open, Number(expected)) : {}));
  const [busy, setBusy] = useState(false);
  const got = Number(metres) || 0;
  const amt = (x) => Math.max(0, Number(amounts[x.lineItemId]) || 0);
  const allocated = r2(open.reduce((t, x) => t + amt(x), 0));
  const extra = r2(got - allocated);
  const over = open.find((x) => amt(x) > x.need + 1e-6);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/v9/production-batches/${encodeURIComponent(batchId)}/receive`, 'POST', { receivedItems: [{ sku: item.sku, allocations: open.map((x) => ({ lineItemId: x.lineItemId, allocatedQuantity: amt(x) })), toStockQuantity: Math.max(0, extra) }] });
      const capped = (r?.result?.clamped || []).reduce((t, c) => t + (c.asked - c.given), 0);
      toast(`Received ${got} m of ${item.sku}${extra > 0 ? ` · ${extra} m to stock` : ''}${capped > 0 ? ` · ${r2(capped)} m more than the orders needed went to stock` : ''}`);
      setMetres(''); setAmounts({});
      onDone();
    } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  if (!open.length) return <Tag tone="good">All received</Tag>;
  return (
    <div className="od-recv">
      <label className="od-small" htmlFor={`od-rcv-${item.sku}`}>Metres received now</label>
      <input id={`od-rcv-${item.sku}`} className="od-input" inputMode="decimal" value={metres} onChange={(e) => { setMetres(e.target.value); setAmounts(receivePlan(open, Number(e.target.value) || 0)); }} placeholder={`up to ${r2(open.reduce((t, x) => t + x.need, 0))} m still to come`} />
      {got > 0 && open.map((x) => (
        <div key={x.lineItemId} className="od-recv-row">
          <span className="od-mono">#{x.number}</span>
          <span className="od-small">{x.cancelled ? 'order cancelled · ' : ''}needs {x.need} m{x.allocated > 0 ? ` (${x.allocated} of ${x.metres} in)` : ''}</span>
          <input className="od-input od-recv-amt" inputMode="decimal" aria-label={`Metres for #${x.number}`} value={amounts[x.lineItemId] ?? ''} placeholder="0"
            onChange={(e) => setAmounts((p) => ({ ...p, [x.lineItemId]: e.target.value }))} />
        </div>
      ))}
      {got > 0 && <p className="od-small">{allocated} m to orders{extra > 0 ? ` · ${extra} m extra goes to stock` : ''}{extra < 0 ? ' · more given to orders than received' : ''}{over ? ` · #${over.number} needs only ${over.need} m` : ''}</p>}
      {got > 0 && <button className="od-btn od-pri" disabled={busy || extra < 0 || !!over} onClick={submit}>{busy ? 'Saving…' : `Receive ${got} m`}</button>}
    </div>
  );
}

// Batches printed together (one set of print files) are received together: one count per fabric, split across the
// batches oldest batch first, then inside each batch oldest order first (same plan as ReceiveItem). What is left after
// every batch is full goes to stock on the newest batch. Each batch keeps its own receive record (same /receive).
export function splitAcross(parts, got) {
  let left = r2(got);
  const out = parts.map((p) => { const give = r2(Math.max(0, Math.min(p.left, left))); left = r2(left - give); return { ...p, give }; });
  if (left > 0 && out.length) out[out.length - 1] = { ...out[out.length - 1], give: r2(out[out.length - 1].give + left), extra: left };
  return out;
}
function RunReceiveRow({ api, line, toast, onDone }) {
  const [metres, setMetres] = useState('');
  const [busy, setBusy] = useState(false);
  const got = Number(metres) || 0;
  const split = got > 0 ? splitAcross(line.parts, got) : [];
  const submit = async () => {
    setBusy(true);
    try {
      for (const p of split.filter((x) => x.give > 0)) {
        const plan = receivePlan(p.open, p.give);
        const allocations = p.open.map((x) => ({ lineItemId: x.lineItemId, allocatedQuantity: Math.max(0, Number(plan[x.lineItemId]) || 0) }));
        const toOrders = r2(allocations.reduce((t, a) => t + a.allocatedQuantity, 0));
        await api(`/api/v9/production-batches/${encodeURIComponent(p.batchId)}/receive`, 'POST', { receivedItems: [{ sku: p.sku, allocations, toStockQuantity: Math.max(0, r2(p.give - toOrders)) }] });
      }
      toast(`Received ${got} m of ${line.sku}: ${split.filter((x) => x.give > 0).map((x) => `${x.give} m ${x.batchId}`).join(', ')}`);
      setMetres('');
      onDone();
    } catch (e) { toast(e.message, true); onDone(); } finally { setBusy(false); }
  };
  const toCome = r2(line.parts.reduce((t, p) => t + p.left, 0));
  return (
    <li className="od-rr">
      <div className="od-rr-h"><b className="od-mono">{line.sku}</b><span className="od-small">{line.fabric}</span>
        <span className="od-small od-rr-m">{toCome} m to come · {line.parts.map((p) => `${p.left} m ${p.batchId}`).join(' · ')}</span></div>
      <div className="od-rr-in">
        <input className="od-input" inputMode="decimal" aria-label={`Metres of ${line.sku} received`} value={metres} onChange={(e) => setMetres(e.target.value)} placeholder={`${toCome} m`} />
        {got > 0 && <span className="od-small">{split.filter((x) => x.give > 0).map((x) => `${x.give} m → ${x.batchId}${x.extra ? ` (${x.extra} m extra to stock)` : ''}`).join(' · ')}</span>}
        {got > 0 && <button className="od-btn od-pri" disabled={busy} onClick={submit}>{busy ? 'Saving…' : `Receive ${got} m`}</button>}
      </div>
    </li>
  );
}
function RunReceive({ api, run, toast, onDone }) {
  const [batches, setBatches] = useState(null);
  const [err, setErr] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let alive = true;
    Promise.all(run.batchIds.map((id) => api(`/api/v1/order-desk/batches/${encodeURIComponent(id)}`).then((r) => r.batch)))
      .then((x) => alive && setBatches(x.sort((a, b) => (a.createdMs || 0) - (b.createdMs || 0))))
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, run, reload]);
  if (err) return <p className="od-small od-late">{err}</p>;
  if (!batches) return <div className="od-skel" aria-busy="true" />;
  const lines = run.lines.map((l) => {
    const parts = batches.map((bt) => {
      const i = bt.items.find((x) => !x.unavailable && String(x.sku).toUpperCase() === String(l.sku).toUpperCase());
      if (!i || i.received >= i.metres - 1e-6) return null;
      const open = i.orders.filter((x) => x.lineItemId && x.allocated < x.metres - 1e-6).map((x) => ({ ...x, need: r2(x.metres - x.allocated) }));
      return { batchId: bt.id, sku: i.sku, left: r2(i.metres - i.received), open };
    }).filter(Boolean);
    return { sku: l.sku, fabric: l.fabricName, parts };
  }).filter((l) => l.parts.length);
  const done = () => { setReload((n) => n + 1); onDone(); };
  if (!lines.length) return <p className="od-small">Everything printed in this run is received.</p>;
  return <ul className="od-rr-list">{lines.map((l) => <RunReceiveRow key={l.sku} api={api} line={l} toast={toast} onDone={done} />)}</ul>;
}
// Fabric-wise metres for this batch (same as the PDF's fabric summary): how much of each base fabric to arrange,
// with what is still to come once part of it is in
function FabricNeed({ items, title = null, leftLabel = 'Still to come' }) {
  const rows = Object.values(items.reduce((acc, i) => {
    const k = i.fabric || 'Fabric not set';
    const x = acc[k] || (acc[k] = { fabric: k, designs: 0, metres: 0, left: 0 });
    x.designs += 1; x.metres += i.metres; x.left += Math.max(0, i.metres - i.received);
    return acc;
  }, {})).sort((a, b) => b.metres - a.metres || a.fabric.localeCompare(b.fabric));
  if (!rows.length) return null;
  const r1 = (n) => Math.round(n * 10) / 10;
  const anyIn = rows.some((x) => x.left < x.metres - 1e-6);
  const tot = rows.reduce((t, x) => ({ designs: t.designs + x.designs, metres: t.metres + x.metres, left: t.left + x.left }), { designs: 0, metres: 0, left: 0 });
  return (
    <section className="od-fneed" aria-label="Fabric needed">
      {title && <div className="od-fneed-h"><b>{title} <ReportFlag section="fabrics" /></b><span className="od-small">{rows.length} fabric{rows.length === 1 ? '' : 's'} · {r1(tot.metres)} m</span></div>}
      <table>
        <thead><tr><th>Fabric</th><th className="num">Designs</th><th className="num">Metres</th>{anyIn && <th className="num">{leftLabel}</th>}</tr></thead>
        <tbody>{rows.map((x) => (
          <tr key={x.fabric}><td>{x.fabric}</td><td className="num">{x.designs}</td><td className="num">{r1(x.metres)} m</td>{anyIn && <td className={`num${x.left > 1e-6 ? ' left' : ''}`}>{x.left > 1e-6 ? `${r1(x.left)} m` : ''}</td>}</tr>
        ))}</tbody>
        {rows.length > 1 && <tfoot><tr><td>Total</td><td className="num">{tot.designs}</td><td className="num">{r1(tot.metres)} m</td>{anyIn && <td className="num">{r1(tot.left)} m</td>}</tr></tfoot>}
      </table>
    </section>
  );
}

// All orders, newest first, a page at a time (office): open any order to check it
function AllOrdersList({ api, current, onOpen }) {
  const [pages, setPages] = useState([null]); // cursor for each page visited (null = first page)
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setErr(null); setData(null);
    const after = pages[page];
    api(`/api/v1/order-desk/all-orders?limit=50${after ? `&after=${encodeURIComponent(after)}` : ''}`)
      .then((r) => { if (!alive) return; setData(r); if (r.total != null) setTotal(r.total); })
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, pages, page]);
  const go = (d) => {
    if (d > 0 && data?.next) { setPages((p) => (p.length > page + 1 ? p : [...p, data.next])); setPage((n) => n + 1); }
    if (d < 0 && page > 0) setPage((n) => n - 1);
  };
  const pager = (
    <div className="od-pager" role="navigation" aria-label="Pages">
      <button className="od-btn" disabled={page === 0} onClick={() => go(-1)}>← Newer</button>
      <span className="od-small">Page {page + 1}{total ? ` of ${Math.max(1, Math.ceil(total / 50))} · ${total.toLocaleString('en-IN')} orders` : ''}</span>
      <button className="od-btn" disabled={!data?.next} onClick={() => go(1)}>Older →</button>
    </div>
  );
  return (
    <>
      <div className="od-list-h">{pager}</div>
      {err && <div className="od-empty"><b>Couldn't load orders</b>{err}</div>}
      {!data && !err && <div className="od-skel" aria-busy="true" />}
      {data && data.rows.length === 0 && <div className="od-empty">No orders.</div>}
      {data && data.rows.map((r) => (
        <button key={r.id} className="od-row" aria-current={current === r.id} onClick={() => onOpen(r.id)}>
          <span className="od-row-top"><b className="od-mono">#{r.number}</b><span className="od-due">{r.due}</span></span>
          <span className="od-row-sub"><span>{titleCase(r.city) || '—'}</span><span className={`od-pay${r.pay === 'COD' ? ' cod' : ''}`}>{r.pay}</span></span>
          <span className="od-chips">{r.chips.map((c, i) => <Tag key={c} tone={i === 0 ? (r.stepCls === 'bad' ? 'bad' : r.stepCls === 'good' ? 'good' : r.stepCls === 'soon' ? 'warn' : 'plain') : 'plain'}>{c}</Tag>)}</span>
        </button>
      ))}
      {data && data.rows.length > 10 && <div className="od-pager-foot">{pager}</div>}
    </>
  );
}

// Receive batch list: one row per print run (batches printed together arrive in one basket)
function RunView({ api, row, toast, onChanged, onOpenBatch }) {
  const r = row.run;
  return (
    <div className="od-det">
      <header className="od-bh">
        <div className="od-bh-top"><h2>Printed together</h2><Tag>{dt(r.createdAtMs)}</Tag></div>
        <p className="od-bh-meta">
          {r.batchIds.map((id, i) => <React.Fragment key={id}>{i > 0 && ' + '}<button className="od-link od-mono" onClick={() => onOpenBatch(id)}>{id}</button>{r.arrived.includes(id) ? '' : ' (not sent yet)'}</React.Fragment>)}
          {' · '}{row.side}
        </p>
      </header>
      <section className="od-verify acc" aria-label="Receive together">
        <div className="od-verify-h"><b>Receive together</b><span className="od-small">Count each fabric once: it fills the oldest batch first, then the next. Extra goes to stock.</span></div>
        <RunReceive api={api} run={r} toast={toast} onDone={onChanged} />
      </section>
    </div>
  );
}

// Print runs this batch was in: admins see the files sent to print (combined metres and each batch's share);
// the warehouse can receive a run printed together in one go
function PrintRunsBox({ api, b, toast, onDone }) {
  const { can } = React.useContext(DeskCtx);
  const admin = can('printruns.view');
  const canRecv = can('receive.do') && b.status !== 'Received';
  const [runs, setRuns] = useState(null);
  const [recv, setRecv] = useState(null);
  const [show, setShow] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!b.printRuns || (!admin && !canRecv)) { setRuns([]); return undefined; }
    api(`/api/v1/order-desk/print-runs?batchId=${encodeURIComponent(b.id)}`).then((r) => alive && setRuns(r.runs || [])).catch(() => alive && setRuns([]));
    return () => { alive = false; };
  }, [api, b.id, b.printRuns, admin, canRecv]);
  const list = (runs || []).filter((r) => admin || r.batchIds.length > 1);
  if (!list.length) return null;
  return (
    <section className="od-verify" aria-label="Print runs">
      <div className="od-verify-h"><b>{admin ? 'Print runs' : 'Printed together'}</b><span className="od-small">files sent to print with this batch</span></div>
      {list.map((r) => (
        <div key={r.id} className="od-run">
          <div className="od-run-h">
            <span><b className="od-mono">{r.name}</b> <span className="od-small">{dt(r.createdAtMs, true)} · {String(r.createdBy || '').replace(/@.*/, '')}</span></span>
            <span className="od-small">{r.batchIds.join(', ')} · {r.totals.files} file{r.totals.files === 1 ? '' : 's'} · {r.totals.metres} m{r.totals.reprintParts ? ` · ${r.totals.reprintParts} reprinted` : ''}</span>
            <span className="od-run-act">
              {admin && <button className="od-link" onClick={() => setShow(show === r.id ? null : r.id)}>{show === r.id ? 'Hide files' : 'See files'}</button>}
              {canRecv && r.batchIds.length > 1 && <button className="od-btn" onClick={() => setRecv(recv === r.id ? null : r.id)}>{recv === r.id ? 'Close' : 'Receive together'}</button>}
            </span>
          </div>
          {admin && show === r.id && (
            <table className="od-run-t">
              <thead><tr><th>Fabric</th><th>SKU</th><th className="num">Metres</th><th className="num">Printed</th><th>Batches</th></tr></thead>
              <tbody>{r.lines.map((l) => (
                <tr key={l.sku}><td>{l.fabricName}</td><td className="od-mono">{l.sku}</td><td className="num">{l.metres}</td><td className="num">{l.printMetres}</td>
                  <td className="od-small">{l.parts.map((p) => `${p.batchId} ${p.metres} m`).join(' · ')}</td></tr>
              ))}</tbody>
            </table>
          )}
          {recv === r.id && <RunReceive api={api} run={r} toast={toast} onDone={onDone} />}
        </div>
      ))}
    </section>
  );
}

// Line-level warehouse actions, usable from the list row or the order screen
// Mark used, Order Desk style: no popup. Loads the same stock candidates as V9's MarkUsedModal and posts the same
// mark-used body, with the same rules (one piece up to 15 m, several pieces above, exact metres). The suggested
// piece comes from the shared plan (the queue row's `plan`), else the V9 best fit.
const MAX_ONE_PIECE = 15;
const near = (a, b) => Math.abs(a - b) < 0.0001;
const m1 = (n) => Math.round(Number(n || 0) * 100) / 100;
function suggest(pieces, need, plan) {
  const byId = new Map(pieces.map((p) => [String(p.pieceId), p]));
  const fromPlan = (plan || []).filter((x) => byId.has(String(x.pieceId)) && Number(byId.get(String(x.pieceId)).length) + 1e-9 >= Number(x.qty));
  if (fromPlan.length && near(fromPlan.reduce((t, x) => t + Number(x.qty), 0), need) && (need > MAX_ONE_PIECE || fromPlan.length === 1)) {
    return Object.fromEntries(fromPlan.map((x) => [String(x.pieceId), m1(x.qty)]));
  }
  if (need <= MAX_ONE_PIECE) {
    const best = pieces.filter((p) => Number(p.length) + 1e-9 >= need).sort((a, b) => Number(a.length) - Number(b.length))[0];
    return best ? { [String(best.pieceId)]: m1(need) } : {};
  }
  const out = {}; let rest = need;
  [...pieces].sort((a, b) => Number(b.length) - Number(a.length)).forEach((p) => { if (rest > 1e-9) { const t = Math.min(Number(p.length), rest); out[String(p.pieceId)] = m1(t); rest = m1(rest - t); } });
  return rest > 1e-9 ? {} : out;
}
function PieceChoice({ pieces, need, sel, setSel, idBase }) {
  const [all, setAll] = useState(false);
  const multi = need > MAX_ONE_PIECE;
  const eligible = multi ? pieces : pieces.filter((p) => Number(p.length) + 1e-9 >= need);
  if (!eligible.length) return <p className="od-small">No piece in stock covers {need} m{multi ? '' : ' in one piece'}.</p>;
  if (multi) {
    const total = m1(Object.values(sel).reduce((t, v) => t + Number(v || 0), 0));
    return (
      <div className="od-cut-split">
        {eligible.map((p) => {
          const id = String(p.pieceId); const v = sel[id] ?? '';
          const over = Number(v || 0) > Number(p.length) + 1e-9;
          return (
            <React.Fragment key={id}>
              <span className="od-cut-pc"><b className="od-mono">{id}</b><small>{m1(p.length)} m · {p.location ? `rack ${p.location}` : 'no rack'}</small></span>
              <input id={`${idBase}-${id}`} className="od-input od-cut-qty" inputMode="decimal" value={v} placeholder="0" aria-label={`Metres from ${id}`}
                onChange={(e) => { const n = e.target.value; setSel((s) => { const x = { ...s }; if (!n || Number(n) <= 0) delete x[id]; else x[id] = n; return x; }); }} />
              <small className={over ? 'od-late' : 'od-small'}>{over ? 'more than the piece' : v ? `${m1(Number(p.length) - Number(v))} m left` : 'not used'}</small>
            </React.Fragment>
          );
        })}
        <span className={`od-cut-sum ${near(total, need) ? 'ok' : ''}`}>{total} of {need} m{near(total, need) ? ' · adds up' : total < need ? ` · ${m1(need - total)} m short` : ` · ${m1(total - need)} m too much`}</span>
      </div>
    );
  }
  const chosen = Object.keys(sel)[0];
  const shown = all ? eligible : eligible.filter((p) => String(p.pieceId) === chosen);
  return (
    <div className="od-cut-pieces" role="radiogroup" aria-label="Piece">
      {shown.map((p) => {
        const id = String(p.pieceId); const left = m1(Number(p.length) - need);
        return (
          <button key={id} type="button" role="radio" aria-checked={id === chosen} className="od-cut-pick" onClick={() => setSel({ [id]: m1(need) })}>
            <span className="od-cut-dot" />
            <span className="od-cut-pc"><b className="od-mono">{id}</b><small>{m1(p.length)} m on the piece · {p.location ? `rack ${p.location}` : 'no rack yet'}</small></span>
            <small className={left <= 0 ? 'od-ok' : 'od-small'}>{left <= 0 ? 'used up' : `${left} m left`}</small>
          </button>
        );
      })}
      {eligible.length > 1 && <button type="button" className="od-link" onClick={() => setAll((x) => !x)}>{all ? 'Hide other pieces' : `Use another piece (${eligible.length - 1})`}</button>}
    </div>
  );
}
function CutPanel({ api, orderId, orderNumber, item, plan = null, toast, onDone, onCancel = null, compact = false, title = true }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState({});
  const [compSel, setCompSel] = useState({});
  const [busy, setBusy] = useState(false);
  const idBase = `od-cut-${orderId}-${item.sku}`.replace(/[^\w-]/g, '_');
  useEffect(() => {
    let alive = true;
    setData(null); setErr(null);
    api(`/api/v9/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(item.sku)}/stock-candidates`).then((r) => {
      if (!alive) return;
      const d = r.result || r;
      const need = Number(d.pendingQty || item.pending || item.metres || 0);
      setData({ ...d, need });
      setSel(suggest(d.pieces || [], need, plan));
      const comps = String(d.allocationMode || '').toUpperCase() === 'COMPONENT_STOCK' ? (d.componentCandidates || []) : [];
      setCompSel(Object.fromEntries(comps.map((c) => [String(c.componentSku).toUpperCase(), suggest(c.pieces || [], Number(c.requiredQty), null)])));
    }).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, orderId, item.sku]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div className="od-cut"><p className="od-small">Couldn't load the pieces: {err}</p></div>;
  if (!data) return <div className="od-cut"><p className="od-small">Finding pieces…</p></div>;
  const need = data.need;
  const comps = String(data.allocationMode || '').toUpperCase() === 'COMPONENT_STOCK' ? (data.componentCandidates || []) : [];
  const valid = (pieces, req, s) => {
    const ids = Object.keys(s);
    if (!ids.length || (req <= MAX_ONE_PIECE && ids.length !== 1)) return false;
    let total = 0;
    for (const id of ids) {
      const p = pieces.find((x) => String(x.pieceId) === id); const q = Number(s[id] || 0);
      if (!p || q <= 0 || q > Number(p.length) + 1e-9) return false;
      total += q;
    }
    return near(total, req);
  };
  const ok = need > 0 && (comps.length ? comps.every((c) => valid(c.pieces || [], Number(c.requiredQty), compSel[String(c.componentSku).toUpperCase()] || {})) : valid(data.pieces || [], need, sel));
  const used = comps.length ? [] : Object.keys(sel);
  const label = comps.length ? `Mark used ${need} m (set)` : used.length === 1 ? `Mark used ${need} m from ${used[0]}` : `Mark used ${need} m from ${used.length} pieces`;
  const submit = async () => {
    const consume = comps.length
      ? comps.flatMap((c) => { const k = String(c.componentSku).toUpperCase(); return Object.entries(compSel[k] || {}).map(([id, q]) => ({ componentSku: k, stockPieceId: id, stockSku: String((c.pieces || []).find((p) => String(p.pieceId) === id)?.stockSku || k).toUpperCase(), qty: Number(q) })); })
      : Object.entries(sel).map(([id, q]) => ({ stockPieceId: id, stockSku: String((data.pieces || []).find((p) => String(p.pieceId) === id)?.stockSku || item.sku).toUpperCase(), qty: Number(q) }));
    setBusy(true);
    try {
      await api(`/api/v9/orders/${encodeURIComponent(orderId)}/mark-used`, 'POST', { items: [{ sku: item.sku, consume: consume.filter((x) => x.stockPieceId && x.qty > 0) }] });
      toast(`Marked used: ${need} m of ${item.sku} for #${orderNumber}${used.length === 1 ? ` from ${used[0]}` : ''}`);
      onDone?.();
    } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  return (
    <div className={`od-cut${compact ? ' compact' : ''}`} onClick={(e) => e.stopPropagation()}>
      {title && <div className="od-cut-h"><b>Mark used {need} m{compact || title === 'short' ? '' : ` of ${data.productName || item.name || item.sku}`}</b><span className="od-small">{need > MAX_ONE_PIECE ? 'more than 15 m: several pieces allowed' : 'one piece'}</span></div>}
      {comps.length ? comps.map((c) => {
        const k = String(c.componentSku).toUpperCase();
        return (
          <div key={k} className="od-cut-comp"><small className="od-mono">{k} · {c.requiredQty} m</small>
            <PieceChoice pieces={c.pieces || []} need={Number(c.requiredQty)} sel={compSel[k] || {}} setSel={(f) => setCompSel((s) => ({ ...s, [k]: typeof f === 'function' ? f(s[k] || {}) : f }))} idBase={`${idBase}-${k}`} />
          </div>
        );
      }) : <PieceChoice pieces={data.pieces || []} need={need} sel={sel} setSel={setSel} idBase={idBase} />}
      <div className="od-cut-f">
        {onCancel && <button type="button" className="od-btn" onClick={onCancel} disabled={busy}>Cancel</button>}
        <button type="button" className={`od-btn od-pri${compact ? '' : ' od-big'}`} disabled={!ok || busy} onClick={submit}>{busy ? 'Cutting…' : label}</button>
      </div>
    </div>
  );
}

// a button that opens the cut panel in place (fabric cards, list rows)
function MarkUsedButton({ api, orderId, orderNumber, item, toast, onDone, plan = null, label = 'Mark used' }) {
  const [open, setOpen] = useState(false);
  if (!open) return <button className="od-btn od-pri" onClick={(e) => { e.stopPropagation(); setOpen(true); }}>{label}</button>;
  return <CutPanel api={api} orderId={orderId} orderNumber={orderNumber} item={item} plan={plan} toast={toast} compact onCancel={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }} />;
}

// who is using the desk (managers see undo actions)
// can(key): Order Desk access (Admin → User management → Order Desk access). Without an access object
// (tests, old callers) everything is allowed; the server checks every action anyway.
const DeskCtx = React.createContext({ isManager: false, can: () => true, BatchTools: null });
// A panel the user may only look at: shown without its buttons and inputs
function Gate({ allow, children }) { return allow ? children : <div className="od-ro">{children}</div>; }

// Restock, Order Desk style: no popup. Same V9 calls and rules as RestockPickerModal: /restock/candidates
// (what to put back), /restock/piece (save), /restock/pieces + /restock/undo-piece (already restocked, undo).
// A returned piece needs a reason (RTO pre-filled when the courier sent it back); damaged metres go to a -D piece.
const RESTOCK_REASONS = ['RTO', 'Customer Return', 'Damaged in Transit', 'Wrong / Incorrect Item', 'Size / Fit Issue', 'Quality Issue', 'Refused Delivery', 'Other'];
function RestockPanel({ api, orderId, orderNumber, item, toast, onDone, onCancel = null, compact = false, title = true }) {
  const { can } = React.useContext(DeskCtx);
  const isManager = can('restock.undo');
  const [line, setLine] = useState(null);
  const [done, setDone] = useState([]);
  const [err, setErr] = useState(null);
  const [reason, setReason] = useState({});
  const [dmgOn, setDmgOn] = useState({});
  const [dmgQty, setDmgQty] = useState({});
  const [shipped, setShipped] = useState('');
  const [busy, setBusy] = useState(false);
  const [undoing, setUndoing] = useState('');
  const [reload, setReload] = useState(0);
  const base = `/api/v9/orders/${encodeURIComponent(orderId)}/restock`;
  const idBase = `od-rs-${orderId}-${item.sku}`.replace(/[^\w-]/g, '_');
  useEffect(() => {
    let alive = true;
    setErr(null);
    Promise.allSettled([api(`${base}/candidates`), api(`${base}/pieces?sku=${encodeURIComponent(item.sku)}`)]).then(([c, p]) => {
      if (!alive) return;
      if (p.status === 'fulfilled') setDone(p.value.result?.pieces || []);
      if (c.status === 'fulfilled') {
        const lines = c.value.result?.lines || [];
        const m = lines.find((l) => String(l.sku).toUpperCase() === String(item.sku).toUpperCase()) || null;
        setLine(m || { entries: [] });
        const r = {}; (m?.entries || []).forEach((e) => { if (e.role === 'RETURNED') r[e.suggestedPieceId] = m.rto ? 'RTO' : ''; });
        setReason(r); setDmgOn({}); setDmgQty({}); setShipped('');
      } else if (p.status !== 'fulfilled') setErr(c.reason?.message || 'Could not load restock data');
      else setLine({ entries: [] });
    });
    return () => { alive = false; };
  }, [api, base, item.sku, reload]);
  if (err) return <div className="od-cut"><p className="od-small">Couldn't load: {err}</p></div>;
  if (!line) return <div className="od-cut"><p className="od-small">Loading pieces…</p></div>;
  const entries = line.entries || [];
  const picker = line.determination === 'PICKER' && (line.pieces || []).length > 0;
  const total = Math.round(entries.reduce((t, e) => t + Number(e.meters || 0), 0) * 100) / 100;
  const missingReason = entries.some((e) => e.role === 'RETURNED' && !String(reason[e.suggestedPieceId] || '').trim());
  const ok = entries.length > 0 && !missingReason && (!picker || shipped);
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        entries: entries.map((e) => ({
          sku: line.sku, suggestedPieceId: e.suggestedPieceId, meters: e.meters, role: e.role === 'RETURNED' ? 'RETURNED' : 'SURPLUS', origin: e.origin,
          sourceBatchId: e.sourceBatchId || undefined, sourcePieceId: e.sourcePieceId || undefined,
          returnReason: e.role === 'RETURNED' ? (reason[e.suggestedPieceId] || undefined) : undefined,
          damagedQty: dmgOn[e.suggestedPieceId] ? Math.max(0, Math.min(Number(dmgQty[e.suggestedPieceId] || 0), e.meters)) : 0,
        })),
      };
      if (picker && shipped) body.shippedPieceId = shipped;
      await api(`${base}/piece`, 'POST', body);
      toast(`Restocked ${total} m of ${item.sku} for #${orderNumber}`);
      onDone?.();
    } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  const undo = async (pieceId) => {
    setUndoing(pieceId);
    try { await api(`${base}/undo-piece`, 'POST', { sku: item.sku, pieceId }); toast(`Restock of ${pieceId} undone`); setReload((n) => n + 1); onDone?.({ keepOpen: true }); } catch (e) { toast(e.message, true); } finally { setUndoing(''); }
  };
  return (
    <div className={`od-cut od-rs${compact ? ' compact' : ''}`} onClick={(e) => e.stopPropagation()}>
      {title && <div className="od-cut-h"><b>Restock {total} m{compact || title === 'short' ? '' : ` of ${item.name || item.sku}`}</b>{line.rto && <span className="od-tag od-warn">RTO · returned</span>}</div>}
      {picker && (
        <div className="od-rs-pick" role="radiogroup" aria-label="Piece shipped to the customer">
          <small>Several pieces were in hand when this shipped. Which one went to the customer?</small>
          {(line.pieces || []).map((p, i) => {
            const pid = p.origin === 'STOCK' ? (p.pieceId || `stock-${i}`) : (p.batchId || `batch-${i}`);
            return (
              <button key={pid} type="button" role="radio" aria-checked={shipped === pid} className="od-cut-pick" onClick={() => setShipped(pid)}>
                <span className="od-cut-dot" /><span className="od-cut-pc"><b className="od-mono">{p.origin === 'STOCK' ? `Stock ${p.pieceId || ''}` : `Batch ${p.batchId || ''}`}</b><small>{p.meters} m</small></span><span />
              </button>
            );
          })}
        </div>
      )}
      {entries.length === 0 && <p className="od-small">Nothing left to restock on this line.</p>}
      {entries.map((e) => {
        const pid = e.suggestedPieceId;
        const on = !!dmgOn[pid];
        const dmg = on ? Math.max(0, Math.min(Number(dmgQty[pid] || 0), e.meters)) : 0;
        const back = Math.round((e.meters - dmg) * 100) / 100;
        const ret = e.role === 'RETURNED';
        return (
          <div key={pid} className="od-rs-piece">
            <div className="od-rs-top">
              <span className="od-rs-id"><b className="od-mono">{pid}</b><span className={`od-tag ${ret ? 'od-warn' : 'od-plain'}`}>{ret ? 'Returned' : 'Extra'}</span></span>
              <small className={dmg > 0 ? 'od-small' : 'od-ok'}>{back} m back to stock</small>
            </div>
            <small className="od-rs-from">{e.meters} m {e.origin === 'STOCK' ? `from stock piece ${e.sourcePieceId || ''}` : `from batch ${e.sourceBatchId || ''}`}{ret ? '' : ' · never shipped'}</small>
            <div className="od-rs-fields">
              {ret && (
                <label className="od-rs-f" htmlFor={`${idBase}-r-${pid}`}><small>Reason</small>
                  <select id={`${idBase}-r-${pid}`} className="od-input" value={reason[pid] || ''} onChange={(ev) => setReason((r) => ({ ...r, [pid]: ev.target.value }))}>
                    <option value="">Pick a reason…</option>{RESTOCK_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
              )}
              <label className="od-rs-switch"><input type="checkbox" checked={on} onChange={() => setDmgOn((s) => ({ ...s, [pid]: !s[pid] }))} /> Damaged</label>
              {on && <label className="od-rs-f" htmlFor={`${idBase}-d-${pid}`}><small>Damaged metres (of {e.meters})</small><input id={`${idBase}-d-${pid}`} className="od-input od-cut-qty" type="number" min="0" max={e.meters} step="0.5" value={dmgQty[pid] ?? ''} placeholder="0" onChange={(ev) => setDmgQty((q) => ({ ...q, [pid]: ev.target.value }))} /></label>}
            </div>
            {dmg > 0 && <small className="od-rs-out"><b className="od-late">{dmg} m damaged</b> → piece <span className="od-mono">{pid}-D</span></small>}
          </div>
        );
      })}
      {missingReason && <small className="od-late">Pick a reason for each returned piece.</small>}
      {picker && !shipped && <small className="od-late">Pick the piece that was shipped first.</small>}
      <div className="od-cut-f">
        {onCancel && <button type="button" className="od-btn" onClick={onCancel} disabled={busy}>Cancel</button>}
        <button type="button" className={`od-btn od-pri${compact ? '' : ' od-big'}`} disabled={!ok || busy} onClick={save}>{busy ? 'Restocking…' : `Restock ${total} m`}</button>
      </div>
      {done.length > 0 && (
        <details className="od-rs-done"><summary className="od-small">Already restocked ({done.length})</summary>
          {done.map((p) => (
            <div key={p.pieceId} className="od-rs-done-row">
              <span><span className="od-mono">{p.pieceId}</span> · {p.activeMeters} m{p.damagedMeters > 0 ? ` + ${p.damagedMeters} m damaged` : ''}{p.returnReason ? ` · ${p.returnReason}` : ''}{p.restockedBy ? ` · ${p.restockedBy}` : ''}</span>
              {isManager && <button className="od-link" disabled={!!undoing} onClick={() => undo(p.pieceId)}>{undoing === p.pieceId ? 'Undoing…' : 'Undo'}</button>}
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

function RestockButton({ api, orderId, orderNumber, item, toast, onDone, label = 'Restock' }) {
  const [open, setOpen] = useState(false);
  if (!open) return <button className="od-btn od-btn-restock" onClick={(e) => { e.stopPropagation(); setOpen(true); }}><span aria-hidden="true">↩</span> {label}</button>;
  return <RestockPanel api={api} orderId={orderId} orderNumber={orderNumber} item={item} toast={toast} compact onCancel={() => setOpen(false)} onDone={(x) => { if (!x?.keepOpen) setOpen(false); onDone?.(); }} />;
}

// Process by PO (V9 Orders parity): put this order's fabrics that are not in a batch into their own single-order
// PO batch, instead of waiting for the next quality batch. Same V9 endpoint and rules (Imported / Allocating /
// Production; only metres not in any batch). Fabrics that can be marked used from stock start unticked.
const PO_STATUSES = ['IMPORTED', 'ALLOCATING', 'PRODUCTION'];
function ProcessPoPanel({ api, o, toast, onDone, inStock }) {
  const { can } = React.useContext(DeskCtx);
  const lines = o.items.filter((i) => i.poQty > 0 && i.status !== 'CANCELLED');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [undoAsk, setUndoAsk] = useState(false);
  const po = o.po || {};
  const canUndo = po.undoable && can('po.undo');
  if (!can('po.create') && !canUndo) return null;
  if (!PO_STATUSES.includes(o.status) && !canUndo) return null;
  const defaultSel = () => { const notStock = lines.filter((i) => !inStock(i.sku)).map((i) => i.sku); return new Set(notStock.length ? notStock : lines.map((i) => i.sku)); };
  const chosen = sel || defaultSel();
  const picked = lines.filter((i) => chosen.has(i.sku));
  const metres = Math.round(picked.reduce((t, i) => t + i.poQty, 0) * 10) / 10;
  const toggle = (sku) => { const n = new Set(chosen); if (n.has(sku)) n.delete(sku); else n.add(sku); setSel(n); setAsk(false); };
  const create = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/v9/orders/${encodeURIComponent(o.id)}/process-by-po`, 'POST', { items: picked.map((i) => ({ sku: i.sku, qty: i.poQty })) });
      toast(`PO batch ${r?.result?.batchId || ''} created${r?.result?.zohoPurchaseOrderNumber ? ` · Zoho PO ${r.result.zohoPurchaseOrderNumber}` : ''} for #${o.number}`);
      setOpen(false); setSel(null); setAsk(false); onDone();
    } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  const undo = async () => {
    setBusy(true);
    try { await api(`/api/v9/orders/${encodeURIComponent(o.id)}/undo-process-po`, 'POST', {}); toast(`Process by PO undone: batch ${po.lastBatchId} removed`); setUndoAsk(false); onDone(); } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  return (
    <>
      {can('po.create') && PO_STATUSES.includes(o.status) && lines.length > 0 && (!open
        ? <div className="od-actions"><button className="od-btn" onClick={() => setOpen(true)}>Process by PO…</button><span className="od-small">{lines.length} fabric{lines.length === 1 ? '' : 's'} not in a batch</span></div>
        : (
          <section className="od-verify acc" aria-label="Process by PO">
            <div className="od-verify-h"><b>Process by PO</b><span className="od-small">this order's fabrics in their own PO batch, without waiting for the next batch day</span></div>
            <div className="od-actions">
              <button className="od-link" onClick={() => { setSel(new Set(lines.filter((i) => !inStock(i.sku)).map((i) => i.sku))); setAsk(false); }}>Not in stock only</button>
              <button className="od-link" onClick={() => { setSel(new Set(lines.map((i) => i.sku))); setAsk(false); }}>Select all</button>
            </div>
            <div className="od-po-list">
              {lines.map((i) => (
                <label key={i.sku} className="od-po-row">
                  <input type="checkbox" checked={chosen.has(i.sku)} onChange={() => toggle(i.sku)} />
                  {i.image ? <img src={i.image} alt="" loading="lazy" /> : <span className="od-noimg" />}
                  <b className="od-mono">{i.sku}</b>
                  <span className={`od-small ${inStock(i.sku) ? 'od-ok' : ''}`}>{inStock(i.sku) ? 'in stock: can be marked used' : 'not in stock'}</span>
                  <span className="od-mono">{i.poQty} m</span>
                </label>
              ))}
            </div>
            {!ask ? (
              <div className="od-actions">
                <button className="od-btn od-pri" disabled={!picked.length || busy} onClick={() => setAsk(true)}>Create PO batch · {picked.length} fabric{picked.length === 1 ? '' : 's'} · {metres} m</button>
                <button className="od-btn" disabled={busy} onClick={() => { setOpen(false); setSel(null); }}>Cancel</button>
              </div>
            ) : (
              <div className="od-fixconfirm" role="group" aria-label="Confirm Process by PO">
                <b>Create a PO batch for #{o.number}?</b>
                <span className="od-small">{picked.map((i) => `${i.sku} ${i.poQty} m`).join(' · ')}. A Zoho purchase order is created and these fabrics go to the production house in their own batch.</span>
                <div className="od-actions">
                  <button className="od-btn od-pri" disabled={busy} onClick={create}>{busy ? 'Creating…' : 'Yes, create PO batch'}</button>
                  <button className="od-btn" disabled={busy} onClick={() => setAsk(false)}>Back</button>
                </div>
              </div>
            )}
          </section>
        ))}
      {canUndo && (
        <div className="od-actions">
          {!undoAsk ? <><span className="od-small">PO batch <b className="od-mono">{po.lastBatchId}</b>{po.poNumber ? ` · Zoho PO ${po.poNumber}` : ''} · nothing received yet</span><button className="od-link" onClick={() => setUndoAsk(true)}>Undo Process by PO</button></>
            : <span className="od-small">Remove batch {po.lastBatchId} and its PO lines? Only right after a mistake, before the production house works on it. <button className="od-link" disabled={busy} onClick={undo}>{busy ? 'Undoing…' : 'Yes, undo'}</button><button className="od-link" onClick={() => setUndoAsk(false)}>No</button></span>}
        </div>
      )}
    </>
  );
}

// Stuck & lost parcels: escalate (recorded here, raised in the courier's dashboard: no public API), mark lost, then
// for a lost parcel the customer side (refund by hand in Shopify, or close for COD / returns) and the Delhivery claim.
const PARCEL_HOW = [['delhivery_dashboard', 'Delhivery dashboard'], ['email', 'Email'], ['call', 'Call'], ['other', 'Other']];
const CLAIM_STEPS = [['raised', 'Raised'], ['approved', 'Approved'], ['paid', 'Paid'], ['rejected', 'Rejected']];
const PARCEL_TITLE = { escalate: 'No courier scan: escalate to Delhivery', escalate_again: 'Escalated, still no scan: escalate again', escalated: 'Escalated to Delhivery', possibly_lost: 'Possibly lost: ask Delhivery to confirm' };
function ParcelPanel({ api, o, toast, onDone }) {
  const { can } = React.useContext(DeskCtx);
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ how: 'delhivery_dashboard', ticket: '', note: '', status: 'raised', claimNo: '', amount: '', paidAmount: '', refund: '', noRefund: false });
  const p = o.parcel;
  if (!p) return null;
  const act = async (body, ok) => {
    setBusy(true);
    try { const r = await api(`/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/parcel`, 'POST', body); toast(r?.message || ok); setMode(null); onDone(); } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const cod = /COD/i.test(o.payment.mode || '');
  const awb = o.shipment.awb;
  const copy = () => { try { navigator.clipboard.writeText(awb); toast('AWB copied'); } catch (e) { toast(awb); } };
  // Reship in progress: the order is back in the normal flow; show where it is on the reship steps
  if (p.reship && !p.level) {
    const r = p.reship;
    const atPack = ['READY_TO_PACK'].includes(o.status);
    const sent = ['PACKED', 'AWAITING_SHIPMENT_WEBHOOK', 'IN_TRANSIT', 'DELIVERED', 'CLOSED'].includes(o.status) && o.shipment.awb && o.shipment.awb !== r.fromAwb;
    if (sent) return <section className="od-verify good" aria-label="Reship"><div className="od-verify-h"><b>Reshipped</b><span className="od-small">new AWB {o.shipment.awb} · lost AWB {r.fromAwb || '—'}</span></div></section>;
    const steps = [
      [`Fabric again: ${r.lines.map((l) => `${l.sku} ${l.metres} m`).join(', ')} (mark used or batch)`, atPack],
      [`Delhivery claim for the lost AWB ${r.fromAwb || ''} raised before cancelling it`, !!(p.claim)],
      ['In Shopify: cancel the old fulfillment, then fulfil again with the Delhivery app (new AWB)', false],
      ['Pack here with the new AWB (no second invoice is made)', false],
    ];
    return (
      <section className="od-verify acc" aria-label="Reship">
        <div className="od-verify-h"><b>Reship in progress</b><span className="od-small">started {dt(r.atMs, true)} by {r.by}</span></div>
        <ol className="od-reship-steps">{steps.map(([t, done], k) => <li key={k} className={done ? 'done' : ''}>{done ? '✓ ' : ''}{t}</li>)}</ol>
        <div className="od-actions od-parcel-ref"><span className="od-small">Lost AWB <b className="od-mono">{r.fromAwb || '—'}</b></span><a className="od-link" href="https://one.delhivery.com/" target="_blank" rel="noreferrer">Open Delhivery One ↗</a>{o.shopifyAdminUrl && <a className="od-link" href={o.shopifyAdminUrl} target="_blank" rel="noreferrer">Open in Shopify ↗</a>}</div>
      </section>
    );
  }
  if (p.closed) {
    return <section className="od-verify" aria-label="Lost parcel"><div className="od-verify-h"><b>Closed as lost in transit</b><span className="od-small">{p.closed.lostMetres} m lost · {p.closed.by} · {dt(p.closed.atMs, true)}</span></div>{p.closed.note && <p className="od-small">{p.closed.note}</p>}</section>;
  }
  const seen = p.lastSeen ? `${p.lastSeen.text || 'last scan'}${p.lastSeen.hub ? ` · ${p.lastSeen.hub}` : ''} · ${dt(p.lastSeen.atMs, true)}` : '';
  const lost = p.level === 'lost';
  const escs = (p.escalationsAll || []).slice().reverse();
  return (
    <section className={`od-verify ${lost || p.level === 'possibly_lost' ? 'bad' : 'warn'}`} aria-label={lost ? 'Lost parcel' : 'Stuck parcel'}>
      <div className="od-verify-h">
        <b>{lost ? `Lost ${p.onReturn ? 'on the way back to us' : 'on the way to the customer'}` : PARCEL_TITLE[p.level]}</b>
        <span className="od-small">{lost ? `${p.by === 'courier' ? 'courier says LOST' : 'marked lost by the team'} · ${dt(p.lostAtMs)}` : `no scan for ${p.daysNoScan} days`}</span>
      </div>
      {seen && <p className="od-small">Last scan: {seen}</p>}
      {p.resentInShopify && <p className="od-small od-late">This parcel was sent again from Shopify (new AWB) without Reship here. Press Reship so its fabric is marked used again and no second invoice is made.</p>}
      <div className="od-actions od-parcel-ref">
        <span className="od-small">AWB <b className="od-mono">{awb || '—'}</b></span>
        {awb && <button className="od-link" onClick={copy}>Copy AWB</button>}
        <a className="od-link" href="https://one.delhivery.com/" target="_blank" rel="noreferrer">Open Delhivery One ↗</a>
      </div>
      {escs.length > 0 && <ul className="od-ndr-log" aria-label="Escalations">{escs.map((e, k) => <li key={k}><b>Escalated</b> · {(PARCEL_HOW.find((h) => h[0] === e.how) || [null, e.how])[1]}{e.ticket ? ` · ${e.ticket}` : ''} · {e.by} · {dt(e.atMs, true)}{e.note ? ` · ${e.note}` : ''}</li>)}</ul>}
      <Gate allow={can('parcel.act')}>
        {!lost && (
          <>
            <div className="od-actions">
              <button className={`od-btn${p.level === 'escalated' ? '' : ' od-pri'}`} disabled={busy} onClick={() => setMode(mode === 'esc' ? null : 'esc')}>{p.level === 'escalate_again' ? 'Escalated again…' : 'Escalated…'}</button>
              <button className="od-btn" disabled={busy} onClick={() => setMode(mode === 'lost' ? null : 'lost')}>Delhivery confirms lost…</button>
            </div>
            {mode === 'esc' && (
              <div className="od-fixconfirm" role="group" aria-label="Record escalation">
                <span className="od-small">Raise it in the Delhivery dashboard (Support → Raise Issue, or Escalate on the shipment), then note it here.</span>
                <div className="od-actions">
                  <select className="od-input" aria-label="How" value={f.how} onChange={set('how')} style={{ maxWidth: 200 }}>{PARCEL_HOW.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                  <input className="od-input" aria-label="Ticket number" placeholder="Ticket no. (optional)" value={f.ticket} onChange={set('ticket')} style={{ maxWidth: 200 }} />
                </div>
                <input className="od-input" aria-label="Escalation note" placeholder="Note (optional)" value={f.note} onChange={set('note')} />
                <div className="od-actions"><button className="od-btn od-pri" disabled={busy} onClick={() => act({ action: 'escalate', how: f.how, ticket: f.ticket, note: f.note }, 'Escalation recorded')}>Save escalation</button><button className="od-btn" onClick={() => setMode(null)}>Cancel</button></div>
              </div>
            )}
            {mode === 'lost' && (
              <div className="od-fixconfirm" role="group" aria-label="Confirm lost">
                <b>Mark #{o.number} lost?</b>
                <span className="od-small">Only when Delhivery confirms it. Then refund (or close) and raise the claim.</span>
                <input className="od-input" aria-label="Lost note" placeholder="What Delhivery said" value={f.note} onChange={set('note')} />
                <div className="od-actions"><button className="od-btn od-pri" disabled={busy || f.note.trim().length < 3} onClick={() => act({ action: 'confirm_lost', note: f.note }, 'Marked lost')}>Yes, mark lost</button><button className="od-btn" onClick={() => setMode(null)}>Back</button></div>
              </div>
            )}
          </>
        )}
        {lost && (
          <div className="od-parcel-tracks">
            <div className="od-parcel-track">
              <small>Customer</small>
              {cod ? <span className="od-small">COD: nothing was collected, so nothing to refund.</span>
                : p.refund ? <span className="od-small od-ok">Refund recorded{p.refund.amount ? ` · ${inr(p.refund.amount)}` : ''} · {p.refund.by} · {dt(p.refund.atMs)}</span>
                  : (
                    <div className="od-actions">
                      {o.shopifyAdminUrl && <a className="od-link" href={o.shopifyAdminUrl} target="_blank" rel="noreferrer">Refund in Shopify ↗</a>}
                      <input className="od-input od-recv-amt" inputMode="decimal" aria-label="Refund amount" placeholder="₹" value={f.refund} onChange={set('refund')} />
                      <button className="od-btn" disabled={busy} onClick={() => act({ action: 'refund_done', amount: Number(f.refund) || null }, 'Refund recorded')}>Refund done</button>
                    </div>
                  )}
            </div>
            <div className="od-parcel-track">
              <small>Delhivery claim</small>
              {p.claim && <span className="od-small">{(CLAIM_STEPS.find((c) => c[0] === p.claim.status) || [null, p.claim.status])[1]}{p.claim.claimNo ? ` · ${p.claim.claimNo}` : ''}{p.claim.amount ? ` · claimed ${inr(p.claim.amount)}` : ''}{p.claim.paidAmount ? ` · paid ${inr(p.claim.paidAmount)}` : ''} · {dt(p.claim.atMs)}{p.claimDue ? ' · no update for 15 days: follow up' : ''}</span>}
              {mode !== 'claim' ? <button className="od-link" onClick={() => setMode('claim')}>{p.claim ? 'Update claim' : 'Claim raised…'}</button> : (
                <div className="od-fixconfirm" role="group" aria-label="Claim">
                  <span className="od-small">Raise it in Delhivery One: Disputes → Lost & Damages Claims → New Claim (owner/admin login).</span>
                  <div className="od-actions">
                    <select className="od-input" aria-label="Claim status" value={f.status} onChange={set('status')} style={{ maxWidth: 160 }}>{CLAIM_STEPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                    <input className="od-input" aria-label="Claim number" placeholder="Claim no." value={f.claimNo} onChange={set('claimNo')} style={{ maxWidth: 170 }} />
                    <input className="od-input od-recv-amt" inputMode="decimal" aria-label="Claimed amount" placeholder="₹ claimed" value={f.amount} onChange={set('amount')} />
                    {f.status === 'paid' && <input className="od-input od-recv-amt" inputMode="decimal" aria-label="Paid amount" placeholder="₹ paid" value={f.paidAmount} onChange={set('paidAmount')} />}
                  </div>
                  <div className="od-actions"><button className="od-btn od-pri" disabled={busy} onClick={() => act({ action: 'claim', status: f.status, claimNo: f.claimNo, amount: f.amount || undefined, paidAmount: f.paidAmount || undefined }, 'Claim saved')}>Save claim</button><button className="od-btn" onClick={() => setMode(null)}>Cancel</button></div>
                </div>
              )}
            </div>
            {mode === 'reship' && (
              <div className="od-fixconfirm" role="group" aria-label="Reship">
                <b>Send #{o.number} again?</b>
                <span className="od-small">The order goes back to Allocate and its fabric is needed again. Then:</span>
                <ol className="od-reship-steps">
                  <li>Mark used from stock, or it goes in the next batch.</li>
                  <li>{p.claim ? 'Claim recorded: good.' : 'Raise the Delhivery claim for the lost AWB first.'} Then, in Shopify, cancel the old fulfillment and fulfil again with the Delhivery app.</li>
                  <li>Pack here with the new AWB. No second invoice is made.</li>
                </ol>
                {!p.claim && <span className="od-small od-late">No claim recorded yet: cancelling the old fulfillment may cancel its waybill in Delhivery. Raise the claim first.</span>}
                <input className="od-input" aria-label="Reship note" placeholder="Note (optional)" value={f.note} onChange={set('note')} />
                <div className="od-actions"><button className="od-btn od-pri" disabled={busy} onClick={() => act({ action: 'reship', note: f.note }, 'Reship started')}>Yes, reship</button><button className="od-btn" onClick={() => setMode(null)}>Back</button></div>
              </div>
            )}
            {mode !== 'close' ? (
              <div className="od-actions"><button className={`od-btn${p.onReturn ? '' : ' od-pri'}`} disabled={busy} onClick={() => setMode('reship')}>Reship…</button><button className={`od-btn${p.onReturn ? ' od-pri' : ''}`} disabled={busy} onClick={() => setMode('close')}>Close as lost…</button>{p.by === 'team' && <button className="od-link" disabled={busy} onClick={() => act({ action: 'not_lost' }, 'Lost mark removed')}>Not lost after all</button>}</div>
            ) : (
              <div className="od-fixconfirm" role="group" aria-label="Close as lost">
                <b>Close #{o.number} as lost in transit?</b>
                <span className="od-small">The order closes; its fabric is recorded as lost (no restock task). The claim can still be updated afterwards.</span>
                {!cod && !p.refund && <label className="od-small"><input type="checkbox" checked={f.noRefund} onChange={set('noRefund')} /> No refund needed (say why below)</label>}
                <input className="od-input" aria-label="Close note" placeholder="Note" value={f.note} onChange={set('note')} />
                <div className="od-actions"><button className="od-btn od-pri" disabled={busy || (!cod && !p.refund && !f.noRefund) || (f.noRefund && f.note.trim().length < 3)} onClick={() => act({ action: 'close', note: f.note, noRefundNeeded: f.noRefund }, 'Closed as lost')}>Yes, close as lost</button><button className="od-btn" onClick={() => setMode(null)}>Back</button></div>
              </div>
            )}
          </div>
        )}
      </Gate>
    </section>
  );
}

// ---- Phase 5: courier / failed delivery, stuck order fixes, invoices, questions from support
const NDR_ACTION_WORDS = { REATTEMPT: 'Reattempt asked', DEFER: 'New delivery date sent', EDIT_DETAILS: 'New address sent', CALL: 'Customer called' };
const NDR_RESULT_WORDS = { confirmed_by_delhivery: 'Delhivery confirmed', sent_to_delhivery: 'sent to Delhivery', delhivery_failed: 'Delhivery refused', reached: 'reached', no_answer: 'no answer', wrong_number: 'wrong number', dialled: 'dialled' };
function CourierPanel({ api, o, toast, onDone, trk = null }) {
  const { can } = React.useContext(DeskCtx);
  const canNdr = can('failed.ndr');
  const [busy, setBusy] = useState(null);
  const [mode, setMode] = useState(null);
  const [date, setDate] = useState('');
  const [addr, setAddr] = useState({ address: '', pincode: '', phone: '' });
  const [fix, setFix] = useState(null); // hand status fix waiting for its confirmation
  const [fixWhy, setFixWhy] = useState('');
  const ext = String(o.shipment.status || '');
  const notPicked = /not picked/i.test(ext);
  const tj = trk?.journey || null;
  // the courier's scans decide when we have them: a new out-for-delivery after a failed attempt is not "failed" any more
  const failed = tj ? (tj.ndrOpen || (!!o.ndr?.scheduled && tj.stage !== 'ofd')) : (!!(o.ndr && (o.ndr.active || o.ndr.scheduled)) || /^pending$/i.test(ext));
  const lastTry = tj?.attempts?.length ? tj.attempts[tj.attempts.length - 1] : null;
  const phone = String(o.shipping?.phone || '').replace(/[^\d+]/g, '');
  const history = (trk?.ndrLog || []).slice(-3).reverse();
  const mismatch = o.status === 'IN_TRANSIT' && /^(delivered|rto)$/i.test(ext);
  const stuck = ['PACKED', 'AWAITING_SHIPMENT_WEBHOOK', 'IN_TRANSIT'].includes(o.status);
  if (!stuck) return null;
  // all is well and the journey card already says where the parcel is: only the two actions, no box
  const quiet = !!tj && !failed && !notPicked && !mismatch && !o.shipment.problem;
  const run = async (key, path, body, okText) => {
    setBusy(key);
    try { const r = await api(path, 'POST', body); toast(r?.message || okText); setMode(null); onDone(); } catch (e) { toast(e.message, true); } finally { setBusy(null); }
  };
  const awb = o.shipment.awb || `HAND-${o.number || o.id}`;
  const isDelhivery = /delhivery/i.test(o.shipment.courier || 'delhivery') && /^\d{10,16}$/.test(o.shipment.awb || '');
  const min = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
  const max = new Date(Date.now() + 6 * 864e5).toISOString().slice(0, 10);
  // Fixing the status by hand overrides the courier: tucked behind "Fix status", one confirmation that names the
  // order and what changes, a reason, and a structured note (who / why / from what) for analytics.
  const no = `#${o.number || o.id}`;
  const FIXES = [
    { key: 'dl', action: 'delivered', label: 'Delivered to customer', path: 'manual-deliver', body: { awb }, ok: 'Marked delivered', ask: `Mark ${no} delivered?`, effect: 'The order is treated as delivered and can close. Delhivery and the customer are not told.' },
    ...(!o.shipment.awb ? [{ key: 'hd', action: 'hand_delivered', label: 'Hand delivered / picked up', path: 'manual-deliver', body: { awb: `HAND-${o.number || o.id}` }, ok: 'Marked hand-delivered', ask: `Mark ${no} hand delivered?`, effect: 'The order is treated as delivered by hand and can close.' }] : []),
    { key: 'ri', action: 'rto_in_progress', label: 'Coming back to us (RTO)', path: 'manual-rto', body: { awb, status: 'RTO_IN_PROGRESS' }, ok: 'Marked returning to us', ask: `Mark ${no} as coming back to us?`, effect: 'The order moves to Coming back and waits for the parcel. Delhivery is not told.' },
    { key: 'rr', action: 'rto_received', label: 'Already returned to us', path: 'manual-rto', body: { awb, status: 'RTO_RECEIVED' }, ok: 'Marked returned to us', ask: `Mark ${no} returned to us?`, effect: 'Its fabric is treated as back with us and a Restock task opens for the warehouse.' },
  ];
  const doFix = async () => {
    const f = fix;
    setBusy(f.key);
    try {
      const r = await api(`/api/v9/orders/${encodeURIComponent(o.id)}/${f.path}`, 'POST', f.body);
      await api(`/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/notes`, 'POST', { text: `Status fixed by hand: ${f.label}. Why: ${fixWhy.trim()}`, kind: 'status_fix', meta: { action: f.action, fromStatus: o.status, courierStatus: ext || null, courierStage: tj?.stage || null, awb } }).catch(() => {});
      toast(r?.message || f.ok); setFix(null); setFixWhy(''); setMode(null); onDone();
    } catch (e) { toast(e.message, true); } finally { setBusy(null); }
  };
  const fixLink = can('courier.fix') && <button type="button" className="od-link od-fixlink" aria-expanded={mode === 'fix'} onClick={() => { setMode(mode === 'fix' ? null : 'fix'); setFix(null); }}>⋯ Fix status</button>;
  const fixRow = mode === 'fix' && (
    fix ? (
      <div className="od-fixconfirm" role="group" aria-label="Confirm status fix">
        <b>{fix.ask}</b>
        <span className="od-small">{fix.effect}</span>
        <label className="od-label" htmlFor={`od-fix-why-${o.id}`}>Why (required)</label>
        <input id={`od-fix-why-${o.id}`} className="od-input" placeholder="e.g. customer confirmed on call" value={fixWhy} onChange={(e) => setFixWhy(e.target.value)} />
        <div className="od-actions">
          <button className="od-btn od-pri" disabled={fixWhy.trim().length < 3 || !!busy} onClick={doFix}>{busy ? 'Saving…' : 'Yes, ' + fix.label.toLowerCase()}</button>
          <button className="od-btn" disabled={!!busy} onClick={() => setFix(null)}>Back</button>
        </div>
      </div>
    ) : (
      <div className="od-fixpick" role="group" aria-label="Fix status">
        <span className="od-small">Only when the courier status is wrong:</span>
        <div className="od-actions">
          {FIXES.map((f) => <button key={f.key} className="od-btn" disabled={!!busy} onClick={() => { setFix(f); setFixWhy(''); }}>{f.label}</button>)}
        </div>
      </div>
    )
  );
  if (quiet) {
    return (
      <div className="od-courier-quiet" aria-label="Courier">
        <div className="od-actions">
          {(canNdr || can('courier.fix')) && <button className="od-btn" disabled={!!busy} onClick={() => run('r', `/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/refresh-courier`, {}, 'Checked')}>{busy === 'r' ? 'Checking…' : 'Check courier now'}</button>}
          {fixLink}
        </div>
        {fixRow}
      </div>
    );
  }
  return (
    <section className={`od-verify ${notPicked || failed || mismatch || o.shipment.problem ? 'warn' : ''}`} aria-label="Courier">
      <div className="od-verify-h">
        <b>{notPicked ? 'Courier never collected the parcel'
          : failed ? `Delivery failed${lastTry ? ` · attempt ${lastTry.n}` : ''}${(lastTry?.reason || o.ndr?.reason) ? `: ${lastTry?.reason || o.ndr.reason}` : ''}`
            : mismatch ? `Courier says ${/rto/i.test(ext) ? 'returned to us' : 'delivered'}: order not updated`
              : o.shipment.problem ? o.shipment.problem
                : tj?.stage === 'ofd' ? `Out for delivery${tj.attempts.length ? ` again (attempt ${tj.attempts.length + 1})` : ''}`
                  : `With the courier · ${tj?.latest?.text || ext || 'no status yet'}`} <ReportFlag section="courier" /></b>
        <span className="od-small">{o.ndr?.scheduled && failed ? `reattempt booked ${o.ndr.reattemptDate || ''}` : failed && lastTry ? `${dt(lastTry.atMs, true)}${lastTry.hub ? ` · ${lastTry.hub}` : ''}` : o.shipment.lastChecked ? `checked ${dt(o.shipment.lastChecked, true)}` : ''}</span>
      </div>
      {failed && (history.length > 0 || trk?.ofdWhatsApp) && (
        <ul className="od-ndr-log" aria-label="What was done">
          {history.map((h, k) => <li key={k}><b>{NDR_ACTION_WORDS[h.action] || words(h.action)}</b>{h.date ? ` for ${dIso(h.date)}` : ''}{h.result ? ` · ${NDR_RESULT_WORDS[h.result] || words(h.result)}` : ''} · {h.by || 'team'} · {dt(h.atMs, true)}</li>)}
          {trk?.ofdWhatsApp && !history.length && <li>Out-for-delivery WhatsApp sent to the customer</li>}
        </ul>
      )}
      <div className="od-actions">
        {(canNdr || can('courier.fix')) && <button className="od-btn" disabled={!!busy} onClick={() => run('r', `/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/refresh-courier`, {}, 'Checked')}>{busy === 'r' ? 'Checking…' : 'Check courier now'}</button>}
        {canNdr && isDelhivery && (failed || notPicked) && <button className="od-btn od-pri" disabled={!!busy} onClick={() => run('t', `/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/ndr`, { action: 'reattempt' }, 'Reattempt requested')}>{notPicked ? 'Ask Delhivery to pick up / deliver tomorrow' : 'Reattempt tomorrow'}</button>}
        {canNdr && isDelhivery && failed && <button className="od-btn" disabled={!!busy} onClick={() => setMode(mode === 'date' ? null : 'date')}>Pick a date</button>}
        {canNdr && isDelhivery && failed && <button className="od-btn" disabled={!!busy} onClick={() => setMode(mode === 'addr' ? null : 'addr')}>Update address</button>}
        {canNdr && failed && phone && <a className="od-btn" href={`tel:${phone}`} onClick={() => setMode('call')}>Call customer</a>}
        {fixLink}
      </div>
      {mode === 'date' && (
        <div className="od-actions">
          <label className="od-small" htmlFor={`od-ndr-date-${o.id}`}>Deliver on</label>
          <input id={`od-ndr-date-${o.id}`} type="date" className="od-input" style={{ maxWidth: 180 }} min={min} max={max} value={date} onChange={(e) => setDate(e.target.value)} />
          <button className="od-btn od-pri" disabled={!date || !!busy} onClick={() => run('d', `/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/ndr`, { action: 'reattempt', date }, 'Date sent to Delhivery')}>Send to Delhivery</button>
        </div>
      )}
      {mode === 'addr' && (
        <div className="od-grid2">
          <label className="od-label" htmlFor={`od-ndr-a-${o.id}`}>New address</label>
          <textarea id={`od-ndr-a-${o.id}`} rows={2} className="od-input" value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} />
          <div className="od-actions">
            <input className="od-input" style={{ maxWidth: 120 }} aria-label="Pincode" placeholder="Pincode" inputMode="numeric" maxLength={6} value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value.replace(/\D/g, '') })} />
            <input className="od-input" style={{ maxWidth: 170 }} aria-label="Phone" placeholder="Phone (optional)" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} />
            <button className="od-btn od-pri" disabled={addr.address.trim().length < 10 || !!busy} onClick={() => run('a', `/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/ndr`, { action: 'address', ...addr }, 'Address sent to Delhivery')}>Send to Delhivery</button>
          </div>
        </div>
      )}
      {mode === 'call' && (
        <div className="od-actions" aria-label="Call outcome">
          <span className="od-small">How did the call go?</span>
          <button className="od-btn" disabled={!!busy} onClick={() => run('c1', `/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/ndr`, { action: 'call', outcome: 'reached' }, 'Call logged')}>Reached</button>
          <button className="od-btn" disabled={!!busy} onClick={() => run('c2', `/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/ndr`, { action: 'call', outcome: 'no_answer' }, 'Call logged')}>No answer</button>
          <button className="od-btn" disabled={!!busy} onClick={() => run('c3', `/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/ndr`, { action: 'call', outcome: 'wrong_number' }, 'Call logged')}>Wrong number</button>
        </div>
      )}
      {fixRow}
    </section>
  );
}

// Why the invoice needs a review, in plain words: metres billed vs what the order needs, per fabric
const fm = (n) => `${Math.round(Math.abs(Number(n) || 0) * 100) / 100} m`;
function InvoiceReason({ inv }) {
  if (!inv) return null;
  const c = inv.coverage;
  const rows = c?.mismatches || [];
  const short = c && c.delta < 0; const over = c && c.delta > 0;
  const head = !c ? null
    : c.state === 'NONE' || (c.invoiced === 0 && c.required > 0) ? `No invoice covers this order yet: ${fm(c.required)} needs billing.`
      : short ? `The invoice bills ${fm(c.invoiced)} but the order needs ${fm(c.required)}: ${fm(c.delta)} not billed.`
        : over ? `The invoice bills ${fm(c.invoiced)} but the order needs only ${fm(c.required)}: ${fm(c.delta)} billed extra.`
          : null;
  const p = inv.parity;
  return (
    <div className="od-inv-why">
      <span className="od-inv-k">Reason</span>
      {head ? <p>{head}</p> : !p && <p>Zoho and the order don't match. Refresh from Zoho to re-check.</p>}
      {rows.length > 0 && (
        <ul>{rows.map((m) => <li key={m.sku}><span className="od-mono">{m.sku}</span><span>needs {fm(m.required)} · invoiced {fm(m.invoiced)}</span><b className={m.delta < 0 ? 'od-late' : 'od-inv-over'}>{m.delta < 0 ? `${fm(m.delta)} short` : `${fm(m.delta)} extra`}</b></li>)}</ul>
      )}
      {p && (
        <p>Zoho sales order differs from the order{p.missing.length ? ` · missing ${p.missing.join(', ')}` : ''}{p.extra.length ? ` · extra ${p.extra.join(', ')}` : ''}{p.qty.length ? ` · different metres on ${p.qty.map((q) => q.sku || q).join(', ')}` : ''}.</p>
      )}
      {['RESOLVED', 'ACKNOWLEDGED'].includes(inv.review?.status) && <p className="od-small">Marked {inv.review.status === 'RESOLVED' ? 'resolved' : 'accepted'}{inv.review.atMs ? ` on ${dt(inv.review.atMs)}` : ''}{inv.review.note ? ` (“${inv.review.note}”)` : ''}, but the invoice in Zoho changed after that, so it is open again.</p>}
      <small>{inv.number ? `Invoice ${inv.number}` : 'Invoice'}{inv.total ? ` · ₹${Math.round(inv.total).toLocaleString('en-IN')}` : ''}{inv.status ? ` · ${inv.status}` : ''}
        {short ? ' · If it was cut or sent short on purpose, accept with a note; otherwise fix the invoice in Zoho.' : over ? ' · Fix the invoice in Zoho, or accept with a note if it is right.' : ''}</small>
    </div>
  );
}

function InvoicePanel({ api, o, toast, onDone }) {
  const review = o.openActions.some((a) => a.type === 'INVOICE_REVIEW_REQUIRED');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null);
  if (!review && !o.zoho.pending) return null;
  const run = async (key, path, body, okText) => {
    setBusy(key);
    try { await api(path, 'POST', body); toast(okText); setNote(''); onDone(); } catch (e) { toast(e.message, true); } finally { setBusy(null); }
  };
  const base = `/api/v9/orders/${encodeURIComponent(o.id)}`;
  return (
    <section className="od-verify warn" aria-label="Invoice and Zoho">
      <div className="od-verify-h"><b>{o.zoho.pending ? 'No Zoho sales order yet' : 'Invoice needs a review'} <ReportFlag section="invoice" /></b><span className="od-small">{o.zoho.invoice?.state ? o.zoho.invoice.state.toLowerCase().replace(/_/g, ' ') : ''}</span></div>
      {o.zoho.pending ? (
        <div className="od-actions"><button className="od-btn od-pri" disabled={!!busy} onClick={() => run('so', `${base}/zoho/create-sales-order`, {}, 'Zoho sales order created')}>Create Zoho sales order</button></div>
      ) : (
        <>
          <InvoiceReason inv={o.zoho.invoice} />
          <div className="od-actions"><button className="od-btn" disabled={!!busy} onClick={() => run('rf', `${base}/invoice/refresh`, {}, 'Invoice refreshed from Zoho')}>Refresh from Zoho</button></div>
          <label className="od-label" htmlFor={`od-inv-${o.id}`}>Note (why it is OK)</label>
          <input id={`od-inv-${o.id}`} className="od-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. ₹14 rounding difference, accepted" />
          <div className="od-actions">
            <button className="od-btn od-pri" disabled={!!busy || note.trim().length < 3} onClick={() => run('ak', `${base}/invoice/acknowledge`, { note }, 'Difference accepted')}>Accept the difference</button>
            <button className="od-btn" disabled={!!busy || note.trim().length < 3} onClick={() => run('rs', `${base}/invoice/resolve`, { note }, 'Marked resolved')}>Fixed in Zoho · resolve</button>
          </div>
        </>
      )}
    </section>
  );
}

function QuestionsPanel({ api, o, toast, onDone }) {
  const [text, setText] = useState({});
  const [busy, setBusy] = useState(null);
  if (!o.questions?.length) return null;
  const answer = async (q) => {
    setBusy(q.id);
    try { await api(`/api/v1/order-desk/questions/${q.id}/answer`, 'POST', { text: text[q.id] }); toast('Answer sent to the support conversation'); onDone(); } catch (e) { toast(e.message, true); } finally { setBusy(null); }
  };
  return (
    <section className="od-verify acc" aria-label="Questions from support">
      <b>Questions from support</b>
      {o.questions.map((q) => (
        <div key={q.id} className="od-q-item">
          <p><b>{q.askedBy}</b> <span className="od-small">{dt(q.askedAtMs, true)}</span></p>
          <p>{q.question}</p>
          {q.status === 'answered' ? <p className="od-small">Answered by {q.answeredBy}: {q.answer}</p> : (
            <>
              <label className="od-label" htmlFor={`od-ans-${q.id}`}>Your answer (goes into the customer conversation as a team note)</label>
              <textarea id={`od-ans-${q.id}`} rows={2} className="od-input" value={text[q.id] || ''} onChange={(e) => setText({ ...text, [q.id]: e.target.value })} placeholder="e.g. Printing done, dispatch tomorrow" />
              <button className="od-btn od-pri" disabled={busy === q.id || (text[q.id] || '').trim().length < 2} onClick={() => answer(q)}>Send answer</button>
            </>
          )}
        </div>
      ))}
    </section>
  );
}

// Where invoices are created (order flow step 5). Managers switch it; every switch is logged. Both steps bill only
// fabric not yet invoiced, so switching never double-bills and never misses an invoice.
function InvoiceSetting({ api }) {
  const { can } = React.useContext(DeskCtx);
  const [d, setD] = useState(null);
  const [ask, setAsk] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => api('/api/v1/order-desk/flow-settings').then(setD).catch((e) => setMsg(e.message)), [api]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const LABEL = { ship: 'When the parcel ships (Shopify fulfillment)', pack: 'At packing (Mark packed / Pack & print)' };
  const save = async () => {
    setBusy(true);
    try { await api('/api/v1/order-desk/flow-settings', 'POST', { invoiceAt: ask }); setAsk(null); setMsg('Saved: takes effect within a minute.'); load(); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  return (
    <section className="od-card" aria-label="Invoice setting">
      <h3>Invoices are created</h3>
      <p><b>{LABEL[d.invoiceAt]}</b></p>
      <p className="od-small">Either way each fabric is invoiced once: the other step bills only what is still un-invoiced, and the nightly check lists anything shipped without an invoice.{d.invoiceAt === 'ship' && d.packInvoiceGate === 'shadow' ? ' Trial: Mark packed still invoices while we compare (no change for the team yet).' : ''}</p>
      {can('settings.edit') && !ask && <button className="od-btn" onClick={() => setAsk(d.invoiceAt === 'ship' ? 'pack' : 'ship')}>Change to: {d.invoiceAt === 'ship' ? 'at packing' : 'when the parcel ships'}</button>}
      {ask && (
        <div className="od-fixconfirm" role="group" aria-label="Confirm invoice setting">
          <b>Create invoices {ask === 'pack' ? 'at packing' : 'when the parcel ships'}?</b>
          <span className="od-small">{ask === 'pack' ? 'Mark packed will create the invoice for what is packed. Parcels shipped without packing in the app are still invoiced when they ship.' : 'Mark packed stops creating invoices for Shopify orders; the Shopify fulfillment creates them. Custom (-CO) orders still invoice at packing.'}</span>
          <div className="od-actions"><button className="od-btn od-pri" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Yes, change'}</button><button className="od-btn" onClick={() => setAsk(null)}>Cancel</button></div>
        </div>
      )}
      {msg && <p className="od-small">{msg}</p>}
      {d.history?.length > 0 && <p className="od-small">Last change: {d.history[0].from} → {d.history[0].to} by {d.history[0].by} · {dt(d.history[0].atMs, true)}</p>}
    </section>
  );
}

function Overview({ api }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api('/api/v1/order-desk/overview').then((r) => setD(r.overview)).catch((e) => setErr(e.message)); }, [api]);
  if (err) return <div className="od-empty"><b>Overview is for managers</b>{err}</div>;
  if (!d) return <div className="od-skel" aria-busy="true" />;
  const teams = ['office', 'warehouse', 'all'];
  return (
    <div className="od-det">
      <div className="od-det-h"><div><h2>Overview</h2><p className="od-sub">Team load now and where the time goes (last {d.days} days).</p></div></div>
      <InvoiceSetting api={api} />
      <div className="od-cards">
        {teams.map((t) => (
          <section key={t} className="od-card">
            <h3>{TEAM_LABEL[t]}</h3>
            <table className="od-mini"><thead><tr><th>Queue</th><th className="r">Open</th><th className="r">Late</th></tr></thead>
              <tbody>{d.queues.filter((q) => q.team === t && q.id !== 'batches').map((q) => <tr key={q.id}><td>{q.label}</td><td className="r">{q.count}</td><td className={`r${q.late ? ' od-late' : ''}`}>{q.late}</td></tr>)}</tbody></table>
          </section>
        ))}
      </div>
      <section className="od-card">
        <h3>Where the time goes</h3>
        {d.stages.length === 0 ? <p className="od-small">Stage timers started recently: numbers appear as orders move.</p> : (
          <table className="od-mini"><thead><tr><th>Step</th><th className="r">Orders moved on</th><th className="r">Median time in step</th></tr></thead>
            <tbody>{d.stages.map((x) => <tr key={x.status}><td>{x.step}</td><td className="r">{x.orders}</td><td className="r">{x.medianHours >= 48 ? `${Math.round(x.medianHours / 24 * 10) / 10} days` : `${x.medianHours} h`}</td></tr>)}</tbody></table>
        )}
        {d.stageLogSince && <p className="od-small">Recording since {dt(d.stageLogSince, true)}; the picture gets reliable after about two weeks.</p>}
      </section>
      <div className="od-cards">
        <section className="od-card"><h3>Verification</h3><p className="od-small">{d.verification.total ? Object.entries(d.verification.decisions).map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`).join(' · ') : 'Starts when verification is switched on.'}</p></section>
        <section className="od-card"><h3>Production house</h3><p className="od-small">{d.batches.arrived} batches arrived · {d.batches.deliveries} deliveries received · {d.batches.chases} chases</p></section>
      </div>
    </div>
  );
}

// ---- More line / order / batch actions (same server steps as the v9 pages)
function UndoCut({ api, o, item, cut, toast, onDone }) {
  const { can } = React.useContext(DeskCtx);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const undo = async () => {
    setBusy(true);
    try {
      await api(`/api/v9/orders/${encodeURIComponent(o.id)}/undo-mark-used`, 'POST', { items: [{ sku: item.sku, stockPieceId: cut.pieceId, qty: cut.qty }] });
      toast(`Mark used undone: ${cut.qty} m back on piece ${cut.pieceId}`);
      onDone();
    } catch (e) { toast(e.message, true); } finally { setBusy(false); setAsk(false); }
  };
  return (
    <div className="od-small od-cutrec">
      Used {cut.qty} m from piece <b className="od-mono">{cut.pieceId}</b>
      {o.preShip && can('markused.undo') && (!ask ? <button className="od-link" onClick={() => setAsk(true)}>Undo</button>
        : <> · put {cut.qty} m back? <button className="od-link" disabled={busy} onClick={undo}>Yes, undo</button><button className="od-link" onClick={() => setAsk(false)}>No</button></>)}
    </div>
  );
}

const RETURN_REASONS = ['Damaged', 'Wrong fabric sent', 'Colour differs', 'Quality not as expected', 'Size / metres wrong', 'Other'];

function ReturnRequestPanel({ api, o, toast, onDone }) {
  const [open, setOpen] = useState(false);
  const lines = o.items.filter((i) => i.status !== 'CANCELLED' && (i.shipped || i.metres) > 0);
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  if (!['DELIVERED', 'CLOSED'].includes(o.status) || o.returnRequest) return null;
  const items = lines.map((i) => ({ sku: i.sku, qty: Math.min(Number(qty[i.sku]) || 0, i.shipped || i.metres) })).filter((x) => x.qty > 0);
  const send = async () => {
    setBusy(true);
    try {
      await api(`/api/v9/orders/${encodeURIComponent(o.id)}/returns/request`, 'POST', { items, reason: note.trim() ? `${reason}: ${note.trim()}` : reason, source: 'order_desk' });
      toast('Return request created: the order now waits for the parcel');
      setOpen(false);
      onDone();
    } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  if (!open) return <div className="od-actions"><button className="od-btn" onClick={() => setOpen(true)}>Customer return…</button></div>;
  return (
    <section className="od-verify warn" aria-label="Customer return">
      <b>Customer return: which fabrics come back?</b>
      {lines.map((i) => (
        <div key={i.sku} className="od-ret-row">
          <span><b>{i.name || i.sku}</b> <span className="od-small od-mono">{i.sku} · shipped {i.shipped || i.metres} m</span></span>
          <input className="od-input" style={{ maxWidth: 90 }} inputMode="decimal" aria-label={`Metres returned of ${i.sku}`} placeholder="m" value={qty[i.sku] || ''} onChange={(e) => setQty({ ...qty, [i.sku]: e.target.value })} />
        </div>
      ))}
      <label className="od-label" htmlFor={`od-ret-r-${o.id}`}>Reason</label>
      <select id={`od-ret-r-${o.id}`} className="od-input" value={reason} onChange={(e) => setReason(e.target.value)}><option value="">Choose…</option>{RETURN_REASONS.map((r) => <option key={r}>{r}</option>)}</select>
      <input className="od-input" aria-label="Return note" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="od-actions">
        <button className="od-btn od-pri" disabled={busy || !items.length || !reason} onClick={send}>Create return request</button>
        <button className="od-btn" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </section>
  );
}

function ReturnReceivePanel({ api, o, toast, onDone }) {
  const req = o.returnRequest;
  const [qty, setQty] = useState(() => Object.fromEntries((req?.items || []).map((x) => [String(x.sku).toUpperCase(), String(x.qty ?? '')])));
  const [busy, setBusy] = useState(false);
  if (o.status !== 'CUSTOMER_RETURN_IN_PROGRESS' || !req) return null;
  const items = Object.entries(qty).map(([sku, q]) => ({ sku, qty: Number(q) || 0 })).filter((x) => x.qty > 0);
  const receive = async () => {
    setBusy(true);
    try {
      await api(`/api/v9/orders/${encodeURIComponent(o.id)}/returns/${encodeURIComponent(req.id)}/receive`, 'POST', { items });
      toast('Return received: restock the fabric next');
      onDone();
    } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  return (
    <section className="od-verify warn" aria-label="Receive customer return">
      <div className="od-verify-h"><b>Customer return on the way</b><span className="od-small">{req.reason || ''}</span></div>
      {(req.items || []).map((x) => {
        const k = String(x.sku).toUpperCase();
        return (
          <div key={k} className="od-ret-row">
            <span className="od-mono">{k} <span className="od-small">· asked {x.qty} m</span></span>
            <input className="od-input" style={{ maxWidth: 90 }} inputMode="decimal" aria-label={`Metres received of ${k}`} value={qty[k] || ''} onChange={(e) => setQty({ ...qty, [k]: e.target.value })} />
          </div>
        );
      })}
      <button className="od-btn od-pri od-big" disabled={busy || !items.length} onClick={receive}>Return parcel received</button>
    </section>
  );
}

function PostShipCancel({ api, o, toast, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!['PACKED', 'AWAITING_SHIPMENT_WEBHOOK', 'IN_TRANSIT', 'DELIVERED'].includes(o.status)) return null;
  if (o.flags.cancelRequestedPostShip) {
    // say where the cancel came from: Shopify (cancelled there after the label was made) or a person here
    const c = o.cancelPostShip || {};
    const text = c.from === 'shopify' ? `Cancelled in Shopify${c.atMs ? ` on ${dt(c.atMs)}` : ''} (after the courier label was made)`
      : `Cancel requested after dispatch${c.by ? ` · by ${c.by}` : ''}${c.atMs ? ` · ${dt(c.atMs)}` : ''}`;
    return <div className="od-flags"><Tag tone="bad">{text}</Tag>{c.reason && c.from !== 'shopify' && <span className="od-small">{c.reason}</span>}</div>;
  }
  const send = async () => {
    setBusy(true);
    try { await api(`/api/v9/orders/${encodeURIComponent(o.id)}/cancel-request`, 'POST', { reason }); toast('Cancel request recorded'); setOpen(false); onDone(); } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  // Hidden for now (owner 13 Oct): the request only set a flag (no courier/Shopify action, no follow-up list) and it
  // blocked the order from closing. Comes back when it does real work. The pill above still shows existing requests.
  const HIDE_CANCEL_BUTTON = true;
  if (!open) return HIDE_CANCEL_BUTTON ? null : <div className="od-actions"><button className="od-btn od-danger" onClick={() => setOpen(true)}>Customer wants to cancel…</button></div>;
  return (
    <section className="od-verify bad" aria-label="Cancel after dispatch">
      <b>Customer wants to cancel (already dispatched)</b>
      <p className="od-small">This flags the order for the team: stop or return the parcel with the courier, then refund when it is back.</p>
      <input className="od-input" aria-label="Cancel reason" placeholder="Reason (e.g. ordered by mistake)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="od-actions"><button className="od-btn od-danger" disabled={busy || reason.trim().length < 3} onClick={send}>Record cancel request</button><button className="od-btn" onClick={() => setOpen(false)}>Back</button></div>
    </section>
  );
}

// Which road the order is on: normal, sent back by the courier (RTO), returned by the customer, or cancelled.
// steps: [label, tone] where tone 'warn' marks the way back and 'bad' a cancel; now = index of the step in progress.
function orderPath(o) {
  const st = o.status; const from = String(o.closedFrom || '').toUpperCase();
  const base = ['Placed', 'Allocate', 'Production', 'Pack', 'Ship'];
  const w = (x) => [x, 'warn'];
  if (st === 'CANCELLED' || (st === 'CLOSED' && from === 'CANCELLED')) return { steps: [['Placed'], ['Cancelled', 'bad']], now: st === 'CLOSED' ? 2 : 1 };
  if (/^RTO/.test(st) || (st === 'CLOSED' && /^RTO/.test(from))) {
    // once it is coming back, only the way back matters: start at Ship
    const steps = [['Ship'], w('Returning (RTO)'), w('Back with us'), w('Restocked')];
    return { steps, now: st === 'RTO_IN_PROGRESS' ? 1 : st === 'RTO_RECEIVED' ? 3 : 4 };
  }
  if (['CUSTOMER_RETURN_IN_PROGRESS', 'RETURN_RECEIVED'].includes(st) || (st === 'CLOSED' && /RETURN/.test(from))) {
    const steps = [['Delivered'], w('Returning'), w('Return received'), w('Restocked')];
    return { steps, now: st === 'CUSTOMER_RETURN_IN_PROGRESS' ? 1 : st === 'RETURN_RECEIVED' ? 3 : 4 };
  }
  return { steps: [...base, 'Delivered'].map((x) => [x]), now: ['DELIVERED', 'CLOSED', 'POST_CLOSE_REVIEW'].includes(st) ? 6 : (STEP_INDEX[st] ?? 1) };
}

// After shipping the order's road is the courier's: picked up → in transit (hub) → out for delivery → delivered.
// Each failed delivery attempt is an amber step with its reason; "Out again" follows when the courier retries.
const COURIER_ROAD = ['PACKED', 'AWAITING_SHIPMENT_WEBHOOK', 'IN_TRANSIT', 'DELIVERED'];
const onCourierRoad = (o) => COURIER_ROAD.includes(o.status) || (o.status === 'CLOSED' && !/RTO|RETURN|CANCEL/.test(String(o.closedFrom || '').toUpperCase()));
function courierPath(o, j) {
  if (!j || !onCourierRoad(o)) return null;
  const packedMs = (o.timeline || []).find((e) => e.label === 'Packed')?.ms;
  const lastTry = j.attempts[j.attempts.length - 1] || null;
  const steps = [['Packed', null, packedMs ? dt(packedMs) : null], ['Picked up', null, j.picked ? dt(j.picked.atMs) : null], ['In transit', null, j.transit?.hub || null]];
  j.attempts.forEach((a) => steps.push([`Attempt ${a.n} failed`, 'warn', `${dt(a.atMs)} · ${a.reason}`, '!']));
  if (!j.ndrOpen && !j.rto) steps.push([j.attempts.length ? 'Out again' : 'Out for delivery', null, j.ofd && (!lastTry || j.ofd.atMs > lastTry.atMs) ? dt(j.ofd.atMs, true) : null]);
  steps.push(j.rto ? ['Returning (RTO)', 'warn', dt(j.rto.atMs)] : ['Delivered', null, j.delivered ? dt(j.delivered.atMs) : null]);
  const delivered = (j.delivered && !j.rto) || ['DELIVERED', 'CLOSED'].includes(o.status);
  const now = delivered ? steps.length : j.rto || j.ndrOpen || j.stage === 'ofd' ? steps.length - (j.rto ? 1 : 2) : j.stage === 'transit' ? 2 : 1;
  return { steps, now };
}

// Order progress: done steps ticked, the current one ringed, the rest grey, joined by a line.
// steps: [label, tone, sub (date / hub / reason), dot icon]
function Stepper({ steps, now, label = 'Order progress' }) {
  return (
    <ol className={`od-stepper${steps.some((x) => x[2]) ? ' has-sub' : ''}`} aria-label={label}>
      {steps.map(([lab, tone, sub, icon], i) => {
        const state = i < now ? 'done' : i === now ? 'cur' : 'todo';
        return (
          <li key={lab} className={`od-step ${state}${tone ? ` ${tone}` : ''}`} aria-current={state === 'cur' ? 'step' : undefined}>
            <span className="od-step-dot" aria-hidden="true">{tone === 'bad' ? '×' : icon || (state === 'done' ? '✓' : i + 1)}</span>
            <span className="od-step-label">{lab}</span>
            {sub && <span className="od-step-sub" title={sub}>{sub}</span>}
          </li>
        );
      })}
    </ol>
  );
}

// Courier journey card: who/AWB, expected date (late chip), latest scan, and every scan on demand (newest first)
const hubName = (loc) => String(loc || '').replace(/\s*\(.*\)\s*$/, '').replace(/_[A-Z]{1,3}$/, '').replace(/_/g, ' ').trim();
const clock = (ms) => new Date(ms).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' });
function CourierJourney({ trk, busy, onRefresh }) {
  const [open, setOpen] = useState(false);
  const j = trk?.journey;
  if (!trk?.awb) return null;
  const done = j && (j.delivered || j.rto);
  const lateDays = j?.edd && !done ? Math.floor((Date.now() - j.edd) / 864e5) : 0;
  const scans = j ? [...j.scans].reverse() : [];
  let day = null;
  return (
    <section className="od-journey" aria-label="Courier journey">
      <div className="od-journey-h">
        <span className="od-journey-t"><b>{/delhivery/i.test(trk.partner || '') ? 'Delhivery' : titleCase(trk.partner || 'Courier')}</b> <span className="od-mono">{trk.awb}</span></span>
        {j?.origin && j?.destination && <span className="od-small">{hubName(j.origin)} → {titleCase(j.destination)}</span>}
        {lateDays > 0 ? <Tag tone="warn">Late by {lateDays} day{lateDays === 1 ? '' : 's'}</Tag> : j?.edd && !done ? <span className="od-small">Expected {dt(j.edd)}</span> : null}
        <button type="button" className="od-link" disabled={busy} onClick={onRefresh}>{busy ? 'Checking…' : '↻ Refresh'}</button>
      </div>
      {j?.latest
        ? <p className="od-journey-now"><b>{j.latest.text}</b>{j.latest.hub ? ` · ${j.latest.hub}` : ''} · {dt(j.latest.atMs, true)}{trk.source === 'saved' ? ' · from saved updates' : ''}</p>
        : <p className="od-small">No scans from the courier yet.</p>}
      {scans.length > 0 && <button type="button" className="od-link" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? 'Hide scans' : `Show all ${scans.length} scans`}</button>}
      {open && (
        <ol className="od-scans">
          {scans.map((x, k) => {
            const d = dt(x.atMs);
            const head = d !== day ? (day = d) : null;
            const bad = /^EOD-/i.test(x.code || '');
            return (
              <li key={k} className={bad ? 'warn' : /^delivered$/i.test(x.status) ? 'good' : ''}>
                {head && <span className="od-scan-day">{head}</span>}
                <span className="od-scan-row"><time>{clock(x.atMs)}</time><span>{x.text || x.status}</span><small>{hubName(x.loc)}</small></span>
              </li>
            );
          })}
        </ol>
      )}
      {(trk.earlier || []).length > 0 && <p className="od-small">Earlier parcel{trk.earlier.length > 1 ? 's' : ''}: {trk.earlier.map((e) => `${e.awb}${e.status ? ` (${words(e.status)})` : ''}`).join(', ')}</p>}
    </section>
  );
}

// One fabric as an accordion row: closed it says what the fabric needs (pill), open it holds the details and that
// fabric's own action, so it is always clear which fabric is being cut, restocked or received.
const PILL = { act: 'od-pill act', wait: 'od-pill wait', ok: 'od-pill ok', plain: 'od-pill' };
function FabricRow({ id, image, name, sub, hint = '', pill, kind = 'plain', open, onToggle, children }) {
  return (
    <div className={`od-frow${open ? ' open' : ''}${kind === 'act' ? ' act' : ''}`} id={id}>
      <button type="button" className="od-frow-h" aria-expanded={open} onClick={onToggle}>
        <span className="od-chev" aria-hidden="true" />
        {image ? <img src={image} alt="" loading="lazy" /> : <span className="od-noimg" />}
        <span className="od-frow-t" title={hint || undefined}><b className="od-mono">{name}</b>{sub && <small>{sub}</small>}</span>
        <span className={PILL[kind] || PILL.plain}>{kind === 'act' && <span className="od-pill-dot" />}{kind === 'ok' ? '✓ ' : ''}{pill}</span>
      </button>
      {open && <div className="od-frow-b">{children}</div>}
    </div>
  );
}
const ROW_ORDER = { act: 0, wait: 1, plain: 2, ok: 3 };

function OrderView({ api, orderId, onOpenOrder, toast, onChanged, authedFetch, line = null }) {
  const { can, where: reportWhere } = React.useContext(DeskCtx);
  const [o, setO] = useState(null);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const [rtoBusy, setRtoBusy] = useState(false);
  const [openRows, setOpenRows] = useState(null); // null = default (the fabric that needs action now)
  const [showOthers, setShowOthers] = useState(false); // opened from a fabric list: other fabrics folded away
  const [trk, setTrk] = useState(null); // courier journey after shipping
  const [trkBusy, setTrkBusy] = useState(false);
  useEffect(() => { setOpenRows(null); }, [orderId, reload]);
  useEffect(() => { setShowOthers(false); }, [orderId, line?.sku]);
  useEffect(() => {
    let alive = true;
    setErr(null);
    if (!reload) setO(null);
    api(`/api/v1/order-desk/orders/${encodeURIComponent(orderId)}`).then((r) => alive && setO(r.order)).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, orderId, reload]);
  const shipped = !!o && !!o.shipment?.awb && (onCourierRoad(o) || /^RTO/.test(o.status));
  useEffect(() => {
    if (!shipped) { setTrk(null); return undefined; }
    let alive = true;
    api(`/api/v1/order-desk/orders/${encodeURIComponent(orderId)}/tracking`).then((r) => alive && setTrk(r.tracking)).catch(() => alive && setTrk(null));
    return () => { alive = false; };
  }, [api, orderId, reload, shipped]);
  const refreshTrk = async () => {
    setTrkBusy(true);
    try { const r = await api(`/api/v1/order-desk/orders/${encodeURIComponent(orderId)}/tracking?force=1`); setTrk(r.tracking); } catch (e) { toast(e.message, true); } finally { setTrkBusy(false); }
  };
  const afterAction = (otherOrderId) => {
    if (typeof otherOrderId === 'string') { onOpenOrder(otherOrderId); return; }
    setReload((n) => n + 1);
    onChanged?.(otherOrderId && otherOrderId.done ? { done: true } : undefined);
  };
  const doneAction = () => afterAction({ done: true });
  if (err) return <div className="od-empty"><b>Couldn't open this order</b>{err}</div>;
  if (!o) return <div className="od-skel" aria-busy="true" />;
  const actFor = (sku, types) => o.openActions.some((a) => types.includes(a.type) && String(a.sku || '').toUpperCase() === String(sku || '').toUpperCase());
  const rtoReceived = async () => {
    setRtoBusy(true);
    try { await api(`/api/v9/orders/${encodeURIComponent(o.id)}/rto-received`, 'POST', {}); toast('RTO parcel received: restock its fabric next'); afterAction(); } catch (e) { toast(e.message, true); } finally { setRtoBusy(false); }
  };
  const metres = o.items.reduce((s, i) => s + (i.status === 'CANCELLED' ? 0 : i.metres), 0);
  const q = encodeURIComponent(o.number || o.id);
  const links = {
    IMPORTED: [{ label: 'Open this order in V9 Orders', href: `/v9-orders?searchOrderId=${q}` }],
    ALLOCATING: [{ label: 'Mark used / allocate (V9 Orders)', href: `/v9-orders?searchOrderId=${q}` }, { label: 'Create batch (Production)', href: '/batches' }],
    PRODUCTION: [{ label: 'Open batch in Production', href: '/batches' }, { label: 'Open this order in V9 Orders', href: `/v9-orders?searchOrderId=${q}` }],
    READY_TO_PACK: [{ label: 'Pack in Quick Pack', href: '/quick-pack' }, { label: 'Open this order in V9 Orders', href: `/v9-orders?searchOrderId=${q}` }],
  }[o.status] || [{ label: 'Open this order in V9 Orders', href: `/v9-orders?searchOrderId=${q}` }, { label: 'Action Queue', href: '/v9-action-queue' }];
  const addNote = async () => {
    setSaving(true);
    try {
      const r = await api(`/api/v1/order-desk/orders/${encodeURIComponent(o.id)}/notes`, 'POST', { text: note });
      setO((p) => ({ ...p, notes: [...p.notes, r.note] }));
      setNote('');
      toast('Note added');
    } catch (e) { toast(e.message, true); } finally { setSaving(false); }
  };
  const payWord = o.payment.mode === 'COD' ? 'Cash on delivery' : o.payment.mode === 'PREPAID_GATEWAY' ? 'Prepaid online' : words(o.payment.mode) || '—';
  const veryOld = o.promise.late && o.promise.daysLate > 30;
  const preShip = ['IMPORTED', 'ON_HOLD', 'ALLOCATING', 'PRODUCTION', 'READY_TO_PACK'].includes(o.status);
  const dueTag = !o.promise.dispatchBy || !preShip ? null
    : veryOld ? <Tag tone="old">Very old · placed {dt(o.placedMs)}</Tag>
      : o.promise.late ? <Tag tone={o.promise.daysLate > 3 ? 'bad' : 'warn'}>{o.promise.daysLate} day{o.promise.daysLate === 1 ? '' : 's'} late</Tag>
        : <Tag tone="good">Dispatch by {dIso(o.promise.dispatchBy)}</Tag>;
  const ownerWord = o.owner === 'warehouse' ? 'Warehouse' : o.owner === 'auto' ? 'Automatic' : 'Office';
  // each fabric: what it needs (pill) and which action lives in its row
  const fabricRows = o.items.map((i, idx) => {
    const key = `${i.sku}-${idx}`;
    const canCut = actFor(i.sku, ['MARK_USED_ELIGIBLE']) && !o.verifyBlock && can('markused.do');
    const canRestock = actFor(i.sku, ['RESTOCK_PENDING', 'EXCESS_RESTOCK_PENDING']) && can('restock.do');
    const canUndoRestock = actFor(i.sku, ['UNDO_RESTOCK_ELIGIBLE']) && can('restock.undo');
    const batchSrc = (i.sources || []).find((x) => /batch/i.test(x));
    const batchId = (batchSrc || '').match(/[A-Z]{2,}-\d+/)?.[0];
    const st = LINE_STATUS[i.status] || [words(i.status), 'plain'];
    let kind; let pill;
    if (canCut) { kind = 'act'; pill = `Mark used · ${i.pending || i.metres} m`; }
    else if (canRestock) { kind = 'act'; pill = `Restock ${i.returned || i.metres || i.ordered} m`; }
    else if (i.status === 'IN_BATCH') { kind = 'wait'; pill = `At production house${batchId ? ` · ${batchId}` : ''}`; }
    else if (i.status === 'PENDING_ALLOCATION') { kind = 'wait'; pill = 'Next batch'; }
    else if (['RECEIVED', 'RESERVED_FROM_STOCK'].includes(i.status)) { kind = 'ok'; pill = (i.cuts || []).length ? `Used · from ${i.cuts[0].pieceId}` : batchId ? `Ready · from ${batchId}` : 'Ready'; }
    else { kind = st[1] === 'good' ? 'ok' : 'plain'; pill = st[0]; }
    return { i, key, kind, pill, canCut, canRestock, canUndoRestock };
  }).sort((a, b) => (ROW_ORDER[a.kind] ?? 9) - (ROW_ORDER[b.kind] ?? 9));
  const lineSku = line ? String(line.sku).toUpperCase() : null;
  const firstAct = fabricRows.find((r) => r.kind === 'act' && lineSku && String(r.i.sku).toUpperCase() === lineSku) || fabricRows.find((r) => r.kind === 'act');
  const openSet = openRows || new Set(firstAct ? [firstAct.key] : []);
  const toggleRow = (key) => setOpenRows((prev) => { const n = new Set(prev || openSet); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  // opened from a fabric list (Mark used, Restock): show that fabric; the order's other fabrics fold away
  const focusRows = lineSku ? fabricRows.filter((r) => String(r.i.sku).toUpperCase() === lineSku) : [];
  const otherRows = focusRows.length ? fabricRows.filter((r) => !focusRows.includes(r)) : [];
  const visibleRows = focusRows.length && !showOthers ? focusRows : fabricRows;
  const otherWords = [...new Set(otherRows.map((r) => r.pill.replace(/ · .*/, '')))].join(', ');
  // the one thing to do next: the selected line's cut when it came from a line list, else the first line that can be cut
  const cutItem = !o.verifyBlock && (o.items.find((i) => line && String(i.sku).toUpperCase() === String(line.sku).toUpperCase() && actFor(i.sku, ['MARK_USED_ELIGIBLE']))
    || o.items.find((i) => actFor(i.sku, ['MARK_USED_ELIGIBLE'])));
  const restockItem = !cutItem && (o.items.find((i) => line && String(i.sku).toUpperCase() === String(line.sku).toUpperCase() && actFor(i.sku, ['RESTOCK_PENDING', 'EXCESS_RESTOCK_PENDING']))
    || o.items.find((i) => actFor(i.sku, ['RESTOCK_PENDING', 'EXCESS_RESTOCK_PENDING'])));
  // what the next-step box says under the title, by stage
  const nextSub = veryOld ? 'Over a month past the dispatch date. Check the order is still valid before working on it.'
    : restockItem ? 'Put the fabric back on the shelf and record where it went.'
      : preShip ? (o.promise.dispatchBy ? `Dispatch by ${dIso(o.promise.dispatchBy)}${['ALLOCATING', 'IMPORTED'].includes(o.status) ? ` · next batch day ${dIso(o.promise.nextBatchDay)}` : ''}` : '')
        : trk?.journey?.latest ? `Courier: ${trk.journey.latest.text}${trk.journey.latest.hub ? ` · ${trk.journey.latest.hub}` : ''} · ${dt(trk.journey.latest.atMs, true)}`
          : o.shipment.status ? `Courier: ${o.shipment.status}${o.shipment.lastChecked ? ` · checked ${dt(o.shipment.lastChecked, true)}` : ''}` : '';
  const city = titleCase(o.shipping.city);
  const courierExt = String(o.shipment.status || '');
  const courierAlert = !trk?.journey || trk.journey.ndrOpen || /not picked/i.test(courierExt) || !!o.shipment.problem || (o.status === 'IN_TRANSIT' && /^(delivered|rto)$/i.test(courierExt));
  return (
    <div className="od-det od-ov">
      <div className="od-main">
        <ReportArea api={api} toast={toast} kind="order" refId={o.id} number={o.number || o.id} where={reportWhere}>
        <header className="od-head">
          <div className="od-head-l1">
            <h2 className="od-mono">#{o.number || o.id}</h2>
            <span className="od-who">{o.customer.name || o.shipping.name || '—'}{city ? ` · ${city}` : ''}</span>
            {dueTag}
          </div>
          <p className="od-sub od-sub-row"><span>Placed {dt(o.placedMs, true)} · {o.items.length} fabric{o.items.length === 1 ? '' : 's'} · {Math.round(metres * 10) / 10} m · {o.promise.fabricClass} window</span><span className={`od-paypill ${o.payment.mode === 'COD' ? 'cod' : 'pre'}`}>{o.payment.mode === 'COD' ? 'COD' : 'Prepaid'} {inr(o.payment.amount)}</span></p>
          {(() => { const cp = courierPath(o, trk?.journey); const pth = cp || orderPath(o); return <Stepper steps={pth.steps} now={pth.now} label={cp ? 'Courier progress' : 'Order progress'} />; })()}
        </header>

        {/* order-level next step only: fabric work (cut, restock) is done inside its fabric row below */}
        {/* after shipping the courier road + journey card say where it is: no separate next-step box */}
        {!firstAct && !(shipped && trk?.journey) && <section className={`od-next${veryOld ? ' old' : ''}`} aria-label="Next step">
          <div className="od-next-t">
            <small>Next step · {ownerWord} <ReportFlag section="next" /></small>
            <b>{o.step}</b>
            {nextSub && <span className="od-small">{nextSub}</span>}
          </div>
        </section>}

        <Gate allow={can('verify.do')}><VerifyPanel api={api} o={o} onDone={afterAction} toast={toast} /></Gate>
        <Gate allow={can('questions.answer')}><QuestionsPanel api={api} o={o} toast={toast} onDone={afterAction} /></Gate>
        <ParcelPanel api={api} o={o} toast={toast} onDone={afterAction} />
        <ProcessPoPanel api={api} o={o} toast={toast} onDone={afterAction} inStock={(sku) => actFor(sku, ['MARK_USED_ELIGIBLE'])} />
        {o.status === 'READY_TO_PACK' && can('pack.do') && <PackPanel api={api} o={o} toast={toast} onDone={doneAction} />}
        {/* a courier problem (failed attempt, never collected, status mismatch) is acted on first; when all is well the journey card leads */}
        {/* the panel keeps one place (no remount, an open "Fix status" survives the courier data arriving); the card moves */}
        {shipped && !courierAlert && <CourierJourney trk={trk} busy={trkBusy} onRefresh={refreshTrk} />}
        <CourierPanel api={api} o={o} toast={toast} onDone={afterAction} trk={trk} />
        {shipped && courierAlert && <CourierJourney trk={trk} busy={trkBusy} onRefresh={refreshTrk} />}
        <Gate allow={can('rto.receive')}><ReturnReceivePanel api={api} o={o} toast={toast} onDone={afterAction} /></Gate>
        <Gate allow={can('rto.receive')}><ReturnRequestPanel api={api} o={o} toast={toast} onDone={afterAction} /></Gate>
        <PostShipCancel api={api} o={o} toast={toast} onDone={afterAction} />
        <Gate allow={can('invoice.fix')}><InvoicePanel api={api} o={o} toast={toast} onDone={afterAction} /></Gate>
        {o.status === 'RTO_IN_PROGRESS' && o.parcel?.level !== 'lost' && (
          <section className="od-verify warn" aria-label="RTO"><div className="od-verify-h"><b>Coming back to us (RTO)</b><span className="od-small">{o.shipment.status || ''}</span></div>
            {can('rto.receive') && <button className="od-btn od-pri od-big" disabled={rtoBusy} onClick={rtoReceived}>RTO parcel received</button>}</section>
        )}

        {(o.flags.shopifyHold || o.flags.sourceCheck) && (
          <section className="od-verify warn" aria-label="Shopify and Zoho check">
            <div className="od-verify-h"><b>{o.flags.shopifyHold ? 'On hold in Shopify' : 'Check with Shopify / Zoho'}</b><span className="od-small">kept out of batches and Mark used</span></div>
            <p className="od-small">{o.flags.shopifyHold ? 'Release the hold in Shopify when this order can go ahead. Order Desk picks it up within 6 hours.' : o.flags.sourceCheck}</p>
          </section>
        )}
        {(o.flags.onHold || o.flags.deliveryNeedsCheck || o.flags.dedupeBlocked || o.shipment.problem || o.zoho.pending || o.openActions.some((a) => !['MARK_USED_ELIGIBLE', 'RESTOCK_PENDING', 'EXCESS_RESTOCK_PENDING', 'UNDO_RESTOCK_ELIGIBLE', 'PACK_ELIGIBLE'].includes(a.type))) && (
          <div className="od-flags">
            {o.flags.onHold && <Tag tone="bad">On hold in Shopify</Tag>}
            {o.flags.dedupeBlocked && <Tag tone="bad">Possible duplicate (blocked)</Tag>}
            {o.flags.deliveryNeedsCheck && <Tag tone="warn">{o.flags.deliveryNeedsCheck}</Tag>}
            {o.shipment.problem && <Tag tone="warn">{o.shipment.problem}</Tag>}
            {o.zoho.pending && <Tag tone="warn">No Zoho sales order yet</Tag>}
            {[...new Set(o.openActions.filter((a) => !['MARK_USED_ELIGIBLE', 'RESTOCK_PENDING', 'EXCESS_RESTOCK_PENDING', 'UNDO_RESTOCK_ELIGIBLE', 'PACK_ELIGIBLE'].includes(a.type)).map((a) => ACTION_WORD[a.type] || words(a.type)))].map((w) => <Tag key={w} tone="acc">{w}</Tag>)}
          </div>
        )}

        <section className="od-sec">
          <h3>Fabrics ({o.items.length}) <ReportFlag section="fabrics" /></h3>
          {visibleRows.map(({ i, key, kind, pill, canCut, canRestock, canUndoRestock }) => (
            <FabricRow key={key} id={`od-frow-${orderId}-${key}`} image={i.image} name={`${i.sku}${i.bundle ? ' · set' : ''}`} hint={i.name || ''} sub={`${i.metres > 0 ? i.metres : (i.ordered || i.cancelled || 0)} m${i.quality ? ` · ${i.quality}${i.fabric ? ` · ${i.fabric}` : ''}` : ''}`}
              pill={pill} kind={kind} open={openSet.has(key)} onToggle={() => toggleRow(key)}>
              {i.pending > 0 && i.status !== 'CANCELLED' && !canCut && <span className="od-small">{i.pending} m still to allocate</span>}
              {canCut && <CutPanel key={`cut-${i.sku}`} title={false} api={api} orderId={o.id} orderNumber={o.number} item={i} toast={toast} onDone={doneAction}
                plan={lineSku && String(i.sku).toUpperCase() === lineSku ? line.plan : null} />}
              {canRestock && <RestockPanel key={`rs-${i.sku}`} title={false} api={api} orderId={o.id} orderNumber={o.number} item={i} toast={toast} onDone={(x) => { if (!x?.keepOpen) doneAction(); }} />}
              {!canRestock && canUndoRestock && <RestockPanel key={`urs-${i.sku}`} api={api} orderId={o.id} orderNumber={o.number} item={i} title={false} toast={toast} onDone={(x) => { if (!x?.keepOpen) afterAction(); }} />}
              {(i.cancelled > 0 || i.returned > 0 || (i.sources || []).some((x) => !x.startsWith('Stock piece')) || (i.cuts || []).length > 0) && (
                <div className="od-frow-hist">
                  {i.sources.filter((x) => !x.startsWith('Stock piece')).map((x) => <div key={x}>{x}</div>)}
                  {(i.cuts || []).map((c) => <UndoCut key={c.pieceId} api={api} o={o} item={i} cut={c} toast={toast} onDone={afterAction} />)}
                  {i.cancelled > 0 && <div>{i.cancelled} m cancelled</div>}
                  {i.returned > 0 && <div>{i.returned} m returned</div>}
                </div>
              )}
            </FabricRow>
          ))}
          {otherRows.length > 0 && <button type="button" className="od-others" onClick={() => setShowOthers((x) => !x)} aria-expanded={showOthers}>
            {showOthers ? 'Hide the other fabrics' : `Show ${otherRows.length} other fabric${otherRows.length === 1 ? '' : 's'} on this order (${otherWords})`}</button>}
        </section>
        </ReportArea>
      </div>

      <aside className="od-rail" aria-label="Order details">
        <ColResizer varName="--od-rail-w" grow={-1} label="Resize the details panel" />
        <div className="od-card">
          <h3>Customer</h3>
          <p><b>{o.customer.name || '—'}</b></p>
          <div className="od-stats">
            <div><b>{o.customer.orders}</b><span>orders</span></div><div><b>{o.customer.delivered}</b><span>delivered</span></div>
            <div className={o.customer.rto ? 'bad' : ''}><b>{o.customer.rto}</b><span>RTO</span></div><div><b>{o.customer.cancelled}</b><span>cancelled</span></div>
          </div>
          <dl><dt>Spent before</dt><dd>{inr(o.customer.spent)}</dd><dt>Customer since</dt><dd>{o.customer.since}</dd></dl>
          {o.customer.openOrders.length > 0 && <div className="od-small">Other open orders: {o.customer.openOrders.slice(0, 6).map((x) => `#${x.number} (${words(x.status)})`).join(', ')}</div>}
        </div>
        <div className="od-card">
          <h3>Shipping</h3>
          <p>{o.shipping.name || '—'}</p>
          <p className="od-small">{o.shipping.address}{o.shipping.city ? `, ${o.shipping.city}` : ''}{o.shipping.state ? `, ${o.shipping.state}` : ''} {o.shipping.zip}</p>
          <p className="od-small od-mono">{o.shipping.phone || ''}</p>
          <dl>
            <dt>Courier</dt><dd>{o.shipment.courier || 'Not booked'}</dd>
            {o.shipment.awb && <><dt>AWB</dt><dd className="od-mono">{o.shipment.awb}</dd></>}
            {o.shipment.status && <><dt>Courier status</dt><dd>{o.shipment.status}</dd></>}
            {o.shipment.lastChecked && <><dt>Checked</dt><dd>{dt(o.shipment.lastChecked, true)}{o.shipment.checkedVia ? ` · ${o.shipment.checkedVia}` : ''}</dd></>}
          </dl>
        </div>
        <div className="od-card">
          <h3>Payment and Zoho</h3>
          <dl>
            <dt>Paid by</dt><dd>{payWord}</dd>{o.payment.status && !/pending/i.test(o.payment.status) && <><dt>Payment status</dt><dd>{words(o.payment.status)}</dd></>}
            <dt>Order total</dt><dd>{inr(o.payment.amount)}</dd>{o.payment.discount > 0 && <><dt>Discount</dt><dd>{inr(o.payment.discount)}</dd></>}
            <dt>Shipping charged</dt><dd>{inr(o.payment.shipping)}</dd>
            <dt>Zoho SO</dt><dd>{o.zoho.salesOrderId ? <span className="od-ok">{o.zoho.salesOrderNumber || 'Created'}</span> : <span className="od-late">Missing</span>}</dd>
            <dt>Invoice</dt><dd>{o.zoho.invoice ? `${words(o.zoho.invoice.state)}${o.zoho.invoice.numbers.length ? ` · ${o.zoho.invoice.numbers.join(', ')}` : ''}` : 'Not yet (after pack)'}</dd>
            {o.flags.codConfirmation && <><dt>COD</dt><dd>{words(o.flags.codConfirmation)}</dd></>}
          </dl>
        </div>
        <section className="od-card">
          <h3>Timeline</h3>
          <ol className="od-tl">
            {o.timeline.map((e) => <li key={e.label} className={e.ms || e.note === 'created' ? 'done' : ''}>{e.label}<small>{e.ms ? dt(e.ms, true) : e.note || ''}</small></li>)}
            {o.stageLog.map((e) => <li key={e.atMs} className="done">{words(e.from)} → {words(e.to)}<small>{dt(e.atMs, true)} · {e.hoursInFrom} h in previous step</small></li>)}
          </ol>
        </section>
        <section className="od-card">
          <h3>Notes</h3>
          {o.notes.length === 0 && <p className="od-small">No notes yet.</p>}
          {o.notes.map((n) => <p key={n.id} className="od-note">{n.text}<small>{n.by} · {dt(n.atMs, true)}</small></p>)}
          {can('note.add') && <>
          <label className="od-label" htmlFor={`od-note-${o.id}`}>Add a note for the team</label>
          <textarea id={`od-note-${o.id}`} className="od-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          <button className="od-btn" onClick={addNote} disabled={saving || !note.trim()}>{saving ? 'Saving…' : 'Add note'}</button>
          </>}
        </section>
        <section className="od-card">
          <h3>Support conversations</h3>
          {o.conversations.length === 0 ? <p className="od-small">None for this customer.</p> : o.conversations.map((c) => (
            <a key={c.id} className="od-conv" href="/support"><Tag>{CH[c.source] || c.source}</Tag><span>{c.number || ''} {String(c.subject || '').slice(0, 70)}</span><small>{dt(c.lastMs)}</small></a>
          ))}
        </section>
        <div className="od-oldlinks"><span className="od-small">Old pages:</span><Links links={links} /></div>
      </aside>
    </div>
  );
}

const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Create a batch here with the Production page's own preview + create (same rules, same result)
function CreateBatch({ api, quality, toast, onDone }) {
  const { can } = React.useContext(DeskCtx);
  const [preview, setPreview] = useState(null);
  const [excluded, setExcluded] = useState(() => new Set());
  const [startFrom, setStartFrom] = useState('1');
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await api('/api/v9/production-batches/preview', 'POST', { batchQuality: quality });
      const p = r.result || r;
      if (!(p.consolidatedItems || []).length) { toast(`No ${quality} lines can be batched right now`, true); return; }
      setPreview(p);
      setExcluded(new Set());
    } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  const create = async () => {
    const items = preview.consolidatedItems.map((it) => {
      const so = (it.sourceOrders || []).filter((x) => !excluded.has(x.lineItemId));
      return so.length ? { ...it, sourceOrders: so, totalQuantity: so.reduce((t, x) => t + Number(x.quantity || 0), 0) } : null;
    }).filter(Boolean);
    if (!items.length) { toast('Keep at least one line in the batch', true); return; }
    setBusy(true);
    try {
      const body = { batchQuality: quality, consolidatedItems: items, ...(preview.isFirstBatch ? { startFrom } : {}) };
      const r = await api('/api/v9/production-batches', 'POST', body);
      const id = (r.result || r).batchId;
      toast(id ? `Batch ${id} created. The PO goes to the production house at 9 pm.` : 'Batch created');
      setPreview(null);
      onDone();
    } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  if (!can('batch.create')) return null;
  if (!preview) return <div className="od-actions"><button className="od-btn od-pri" onClick={load} disabled={busy}>{busy ? 'Preparing…' : `Create ${quality} batch`}</button></div>;
  const lines = preview.consolidatedItems.flatMap((it) => (it.sourceOrders || []).map((x) => ({ ...x, fabric: it.fabricName, image: it.imageUrl })));
  const kept = lines.filter((x) => !excluded.has(x.lineItemId));
  return (
    <section className="od-card od-create" aria-label="New batch">
      <h3>New {quality} batch · {kept.length} of {lines.length} lines · {Math.round(kept.reduce((t, x) => t + Number(x.quantity || 0), 0) * 10) / 10} m</h3>
      <p className="od-small">Untick any line you want to leave out. Same preview as the Production page.</p>
      {preview.isFirstBatch && <><label className="od-label" htmlFor="od-start">First {quality} batch: start numbering from</label><input id="od-start" className="od-input" value={startFrom} onChange={(e) => setStartFrom(e.target.value)} /></>}
      <div className="od-scroll od-pick">
        {lines.map((x) => (
          <label key={x.lineItemId} className="od-pickrow">
            <input type="checkbox" checked={!excluded.has(x.lineItemId)} onChange={() => setExcluded((p) => { const n = new Set(p); if (n.has(x.lineItemId)) n.delete(x.lineItemId); else n.add(x.lineItemId); return n; })} />
            <span className="od-mono">#{x.orderNumber}</span><span>{x.fabric || x.sku}</span><span className="od-mono">{x.sku}</span><span className="od-mono">{x.quantity} m</span>
          </label>
        ))}
      </div>
      {(preview.ineligibleItems || []).length > 0 && (
        <details><summary className="od-small">{preview.ineligibleItems.length} line{preview.ineligibleItems.length === 1 ? '' : 's'} cannot be batched (why)</summary>
          <ul className="od-small">{preview.ineligibleItems.slice(0, 40).map((x, k) => <li key={k}>#{x.orderNumber} {x.sku}: {x.reason}</li>)}</ul>
        </details>
      )}
      <div className="od-actions">
        <button className="od-btn od-pri" onClick={create} disabled={busy || !kept.length}>{busy ? 'Creating…' : `Create batch (${kept.length} lines)`}</button>
        <button className="od-btn" onClick={() => setPreview(null)} disabled={busy}>Cancel</button>
      </div>
    </section>
  );
}

function BatchDays({ api, days, toast, onDone }) {
  const { can } = React.useContext(DeskCtx);
  const [edit, setEdit] = useState(false);
  const [sel, setSel] = useState(() => new Set(days || []));
  const save = async () => {
    try { await api('/api/v1/order-desk/settings', 'PUT', { batchDays: [...sel] }); toast('Batch days saved'); setEdit(false); onDone(); } catch (e) { toast(e.message, true); }
  };
  if (!edit) return <p className="od-small">Batch days: {(days || []).map((d) => DAY_NAMES[d]).join(', ') || '—'}{can('settings.edit') && <> · <button className="od-link" onClick={() => setEdit(true)}>change</button></>}</p>;
  return (
    <div className="od-actions od-days" role="group" aria-label="Batch days">
      {[1, 2, 3, 4, 5, 6].map((d) => <label key={d}><input type="checkbox" checked={sel.has(d)} onChange={() => setSel((p) => { const n = new Set(p); if (n.has(d)) n.delete(d); else n.add(d); return n; })} /> {DAY_NAMES[d]}</label>)}
      <button className="od-btn od-pri" onClick={save}>Save</button><button className="od-btn" onClick={() => setEdit(false)}>Cancel</button>
    </div>
  );
}

// Production target days per quality ("back from the production house within N working days"): the shared setting
// the Production page uses; the desk's "promised" dates and late flags follow it.
const TARGET_QUALITIES = ['Pure', 'Polyester', 'Mixed'];
function TargetDays({ api, toast }) {
  const { can } = React.useContext(DeskCtx);
  const [t, setT] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { let alive = true; api('/api/v9/production-batches/settings/targets').then((r) => alive && setT(r.targets || {})).catch(() => {}); return () => { alive = false; }; }, [api]);
  if (!t) return null;
  const save = async () => {
    const targets = Object.fromEntries(TARGET_QUALITIES.map((q) => [q, Math.max(1, Math.round(Number(edit[q]) || 0))]));
    setBusy(true);
    try { await api('/api/v9/production-batches/settings/targets', 'POST', { targets }); setT({ ...t, ...targets }); setEdit(null); toast('Target days saved'); } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  if (!edit) return <p className="od-small">Back from the production house within: {TARGET_QUALITIES.map((q) => `${q} ${t[q] ?? t.default ?? '—'}`).join(' · ')} working days{can('settings.targets') && <> · <button className="od-link" onClick={() => setEdit(Object.fromEntries(TARGET_QUALITIES.map((q) => [q, String(t[q] ?? t.default ?? '')])))}>change</button></>}</p>;
  return (
    <div className="od-actions od-days" role="group" aria-label="Target days">
      {TARGET_QUALITIES.map((q) => <label key={q}>{q} <input className="od-input od-recv-amt" inputMode="numeric" aria-label={`${q} target days`} value={edit[q]} onChange={(e) => setEdit({ ...edit, [q]: e.target.value.replace(/\D/g, '') })} /></label>)}
      <span className="od-small">working days</span>
      <button className="od-btn od-pri" disabled={busy} onClick={save}>Save</button><button className="od-btn" onClick={() => setEdit(null)}>Cancel</button>
    </div>
  );
}

function GroupView({ api, row, onOpenOrder, toast, onChanged, batchDays }) {
  const houseUser = React.useContext(DeskCtx).role === 'production'; // no order details for production house staff
  const g = row.group;
  const canBatch = g.quality === 'Pure' || g.quality === 'Polyester';
  return (
    <div className="od-det">
      <div className="od-det-h"><div><h2>{g.quality} · batch now</h2><p className="od-sub">{g.lines.length} lines · {g.metres} m waiting · next batch day {dIso(g.nextBatchDay)}</p></div><Tag tone="acc">Office</Tag></div>
      {canBatch && <BatchDays api={api} days={batchDays} toast={toast} onDone={onChanged} />}
      {canBatch && <TargetDays api={api} toast={toast} />}
      {g.fabrics.length > 0 && <div className="od-flags">{g.fabrics.map(([f, n]) => <Tag key={f}>{f} {n}</Tag>)}</div>}
      <div className="od-box">These lines have no stock piece that fits and are not in a batch yet. {g.quality === 'Plain (from stock)' ? 'Plain fabrics are not printed: mark them used from stock or buy stock.' : g.quality === 'Fabric spec missing' ? 'Add a fabric spec for these SKUs so they can be batched.' : `Creating the ${g.quality} batch sends the PO to the production house at 9 pm.`}</div>
      {canBatch ? <CreateBatch api={api} quality={g.quality} toast={toast} onDone={onChanged} /> : <Links links={[{ label: 'Open Fabric specs', href: '/admin/fabric-specs' }]} />}
      <div className="od-scroll">
        <table className="od-items">
          <thead><tr><th>Order</th><th>Fabric</th><th className="r">Metres</th><th>Dispatch by</th></tr></thead>
          <tbody>
            {g.lines.map((l) => (
              <tr key={l.orderId + l.sku} className={houseUser ? '' : 'od-click'} onClick={houseUser ? undefined : () => onOpenOrder(l.orderId)}>
                <td className="od-mono">#{l.number}{l.cod && <div><Tag tone="warn">COD</Tag></div>}</td>
                <td><div className="od-fab">{l.image ? <img src={l.image} alt="" loading="lazy" /> : <span className="od-noimg" />}<span><b>{l.name || l.sku}</b><small className="od-mono">{l.sku}{l.fabric ? ` · ${l.fabric}` : ''}</small></span></div></td>
                <td className="r">{l.metres} m</td>
                <td>{dIso(l.dispatchBy)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// days after the batch was created ("+3 d"), for each production step
const plusDays = (createdMs, atMs) => (createdMs && atMs ? `+${Math.max(0, Math.floor((atMs - createdMs) / 864e5))} d` : null);

// Mini batch journey for list rows: four segments (created → printing → sent → received) and the current step's
// name beside them, coloured by how it is going. Same steps as the batch screen.
function MiniJourney({ b, stage = null }) {
  const allIn = b.metres > 0 && b.receivedMetres >= b.metres - 1e-6;
  const partly = !allIn && (b.receivedMetres > 0 || b.sends > 0);
  const printed = !!b.printingAtMs || b.receivedMetres > 0 || b.sends > 0;
  const late = !printed && (stage === 'Print pending' || b.houseState === 'print_late');
  const now = allIn ? 4 : partly ? 2 : printed ? 2 : 1;
  const names = ['Created', late ? 'Print pending' : 'Printing', partly ? 'Part sent' : 'Sent to warehouse', 'Received'];
  // after a part, the rest is due one cycle (target days for this batch type) after the first part
  const restLate = partly && (stage === 'Partial overdue' || b.houseState === 'rest_overdue');
  const tone = late || restLate ? 'bad' : partly ? 'warn' : allIn ? 'good' : now === 1 ? 'plain' : 'acc';
  const restOn = b.restDue ? new Date(`${b.restDue}T12:00:00+05:30`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' }) : null;
  const base = allIn ? 'All received' : now === 1 ? (late ? 'Print pending' : 'Not started') : restLate ? 'Partial overdue' : partly ? `Part sent · ${restOn ? `rest due ${restOn}` : `${Math.round((b.receivedMetres / b.metres) * 100)}% in`}` : 'Printing';
  const label = allIn ? base : `${base} · ${plusDays(b.createdMs, Date.now()) || ''}`.replace(/ · $/, '');
  return (
    <span className={`od-mini ${tone}`} aria-label={`Batch progress: ${label}`}>
      <span className="od-mini-bar" aria-hidden="true">{names.map((n, i) => <i key={n} title={n} className={i < now ? 'done' : i === now ? 'cur' : ''} />)}</span>
      <span className="od-mini-label">{label}</span>
    </span>
  );
}

// The production house ticks what is in this delivery before it leaves (one tick per fabric, metres prefilled with
// what is pending and editable for a part); the warehouse then receives exactly that list.
function SendDelivery({ pending, busy, onSend, onCancel }) {
  const [ticked, setTicked] = useState(() => new Set());
  const [m, setM] = useState(() => Object.fromEntries(pending.map((i) => [i.sku, String(Math.round((i.metres - i.received) * 10) / 10)])));
  const left = (i) => Math.round((i.metres - i.received) * 10) / 10;
  const chosen = pending.filter((i) => ticked.has(i.sku));
  const total = Math.round(chosen.reduce((t, i) => t + (Number(m[i.sku]) || 0), 0) * 10) / 10;
  const bad = chosen.find((i) => !(Number(m[i.sku]) > 0) || Number(m[i.sku]) > left(i) + 1e-6);
  const toggle = (sku) => setTicked((p) => { const n = new Set(p); if (n.has(sku)) n.delete(sku); else n.add(sku); return n; });
  return (
    <div className="od-fixconfirm" role="group" aria-label="Send delivery">
      <b>What is in this delivery?</b>
      <div className="od-actions">
        <button className="od-link" onClick={() => setTicked(new Set(pending.map((i) => i.sku)))}>Select all ({pending.length})</button>
        <button className="od-link" onClick={() => setTicked(new Set())}>Clear</button>
      </div>
      <div className="od-po-list">
        {pending.map((i) => (
          <label key={i.sku} className="od-po-row od-send-row">
            <input type="checkbox" checked={ticked.has(i.sku)} onChange={() => toggle(i.sku)} aria-label={`Send ${i.sku}`} />
            {i.image ? <img src={i.image} alt="" loading="lazy" /> : <span className="od-noimg" />}
            <b className="od-mono">{i.sku}</b>
            <span className="od-small">{left(i)} m pending</span>
            <input className="od-input od-recv-amt" inputMode="decimal" aria-label={`Metres of ${i.sku} sent`} value={m[i.sku]} disabled={!ticked.has(i.sku)}
              onChange={(e) => setM((p) => ({ ...p, [i.sku]: e.target.value }))} onClick={(e) => e.stopPropagation()} />
          </label>
        ))}
      </div>
      {bad && <span className="od-small od-late">{bad.sku}: metres must be more than 0 and at most {left(bad)} m</span>}
      <div className="od-actions">
        <button className="od-btn od-pri" disabled={busy || !chosen.length || !!bad} onClick={() => onSend(chosen.map((i) => ({ sku: i.sku, metres: Number(m[i.sku]) })))}>{busy ? 'Sending…' : `Send ${chosen.length} fabric${chosen.length === 1 ? '' : 's'} · ${total} m to warehouse`}</button>
        <button className="od-btn" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// A cancelled order's fabric stays in the batch: it is received when it arrives, then restocked
function PendingNote({ item }) {
  if (!item.orders.length || !item.orders.every((o) => o.cancelled)) return null;
  return <span className="od-small od-restock-note">Order cancelled: receive it when it arrives, then restock</span>;
}

// Received lines: undo is rare and wipes every receive of that fabric, so it sits behind a menu (managers only)
function ReceivedActions({ api, b, item, toast, onDone, isManager }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!isManager || item.received <= 0) return null;
  const undo = async () => {
    setBusy(true);
    try { await api(`/api/v9/production-batches/${encodeURIComponent(b.id)}/undo-receive`, 'POST', { sku: item.sku }); toast('Receive undone'); onDone(); } catch (e) { toast(e.message, true); } finally { setBusy(false); setOpen(false); }
  };
  return !open ? <button className="od-link od-more" aria-label={`More for ${item.sku}`} onClick={() => setOpen(true)}>⋯</button>
    : <span className="od-small">Undo all {item.received} m received for every order? <button className="od-link" disabled={busy} onClick={undo}>Yes, undo</button><button className="od-link" onClick={() => setOpen(false)}>No</button></span>;
}

const HOUSE_STATE = { rest_overdue: ['Partial overdue', 'bad'], print_late: ['Print pending', 'bad'], tail: ['Partial', 'warn'], po_missing: ['PO missing', 'bad'], po_tonight: ['PO tonight 9 pm', 'plain'], not_started: ['Not started', 'plain'], printing: ['Printing', 'acc'], done: ['All in', 'good'] };

function BatchView({ api, batchId, onOpenOrder, toast, onChanged, authedFetch = null, houseView = false }) {
  const { can, BatchTools, role, where: reportWhere } = React.useContext(DeskCtx);
  const houseUser = role === 'production'; // production-house staff: no "usually all in" estimate (office/warehouse only)
  const isManager = can('receive.undo');
  const canRecv = can('receive.do');
  const canHouse = can('house.handover');
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);
  const [reload, setReload] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [fullAsk, setFullAsk] = useState(false);
  const [sending, setSending] = useState(false);
  const [openB, setOpenB] = useState(null); // accordion: null = default (first fabric to receive)
  const [allFabrics, setAllFabrics] = useState(false); // production house: pending fabrics only unless asked
  useEffect(() => { setOpenB(null); setAllFabrics(false); }, [batchId]);
  useEffect(() => {
    let alive = true;
    setErr(null);
    if (!reload) setB(null);
    api(`/api/v1/order-desk/batches/${encodeURIComponent(batchId)}`).then((r) => alive && setB(r.batch)).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, batchId, reload]);
  const act = async (path, body, okText) => {
    setBusy(true);
    try { await api(path, 'POST', body); toast(okText); setNote(''); setReload((n) => n + 1); onChanged?.(); } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  const bump = () => { setReload((n) => n + 1); onChanged?.(); };
  // "Everything arrived in full": first delivery = the batch-wide mark-as-received; after a part delivery that
  // route refuses, so receive each fabric's remaining metres per order instead (same /receive as a single fabric)
  const receiveRest = async () => {
    setBusy(true);
    try {
      const items = b.items.filter((i) => !i.unavailable && i.received < i.metres - 1e-6);
      const anyIn = b.items.some((i) => i.received > 0);
      if (!anyIn) await api(`/api/v9/production-batches/${encodeURIComponent(b.id)}/mark-as-received`, 'POST', {});
      else {
        const receivedItems = items.map((i) => {
          const open = i.orders.filter((x) => x.lineItemId && x.allocated < x.metres - 1e-6);
          const toOrders = open.reduce((t, x) => t + (x.metres - x.allocated), 0);
          return { sku: i.sku, allocations: open.map((x) => ({ lineItemId: x.lineItemId, allocatedQuantity: r2(x.metres - x.allocated) })), toStockQuantity: Math.max(0, r2(i.metres - i.received - toOrders)) };
        });
        await api(`/api/v9/production-batches/${encodeURIComponent(b.id)}/receive`, 'POST', { receivedItems });
      }
      await api(`/api/v1/order-desk/batches/${encodeURIComponent(b.id)}/handover`, 'POST', { action: 'delivery_done' });
      toast('Everything received: batch complete');
      setFullAsk(false); bump();
    } catch (e) { toast(e.message, true); } finally { setBusy(false); }
  };
  if (err) return <div className="od-empty"><b>Couldn't open this batch</b>{err}</div>;
  if (!b) return <div className="od-skel" aria-busy="true" />;
  const active = b.items.filter((i) => !i.unavailable);
  const req = active.reduce((s, i) => s + i.metres, 0);
  const rec = active.reduce((s, i) => s + Math.min(i.received, i.metres), 0);
  const pending = active.filter((i) => i.received < i.metres - 1e-6);
  const received = active.filter((i) => i.received >= i.metres - 1e-6);
  const released = b.items.filter((i) => i.unavailable);
  const arrived = b.handover.state === 'arrived';
  const deliverySent = arrived && b.handover.delivery ? new Map(b.handover.delivery.items.map((d) => [String(d.sku).toUpperCase(), d.metres])) : null;
  const bRows = [
    // at the production house the batch shows only what is still to come; restock notes appear once it arrives
    // with the warehouse: what the production house sent is received (metres prefilled); the rest waits
    ...pending.map((i) => {
      const sentM = deliverySent ? deliverySent.get(String(i.sku).toUpperCase()) : null;
      if (arrived && deliverySent && sentM == null) return { i, kind: 'wait', pill: 'Not in this delivery' };
      return { i, kind: arrived ? 'act' : 'wait', expected: sentM, pill: arrived ? `Receive ${sentM ?? Math.round((i.metres - i.received) * 10) / 10} m${i.orders.length && i.orders.every((o) => o.cancelled) ? ' · then restock' : ''}` : `${Math.round((i.metres - i.received) * 10) / 10} m pending` };
    }),
    ...received.map((i) => ({ i, kind: 'ok', pill: `Received ${i.received} m` })),
    ...released.map((i) => ({ i, kind: 'plain', pill: 'Unavailable' })),
  ];
  const firstB = bRows.find((r) => r.kind === 'act');
  const bOpen = openB || new Set(firstB ? [firstB.i.sku] : []);
  // At production house: no PO details (the office handles a missing PO from All batches)
  const hidePo = houseView && !arrived;
  const st = hidePo && /^po_/.test(b.houseState || '') ? (b.restDue ? HOUSE_STATE.tail : HOUSE_STATE.not_started) : (HOUSE_STATE[b.houseState] || null);
  const today = new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
  // batch header: name + stage + promise, the key numbers, how much is in, then who made it
  const headerEl = (() => {
        const pendingM = Math.round(pending.reduce((s, i) => s + i.metres - i.received, 0) * 10) / 10;
        const open = b.status !== 'Received';
        const dueLate = open && !arrived && b.backBy && b.backBy < today;
        const who = b.createdBy ? String(b.createdBy).replace(/@.*/, '') : null;
        return (
          <header className="od-bh">
            <div className="od-bh-top">
              <h2 className="od-mono">{b.id}</h2>
              {b.createdMs && <Tag>Created {dt(b.createdMs)}</Tag>}
              {st && <Tag tone={st[1]}>{st[0]}{b.houseState === 'tail' ? ` · ${b.tailDays} d since last delivery` : ''}</Tag>}
              {open && b.backBy && <span className={`od-bh-due${dueLate ? ' late' : ''}`}>{dueLate ? 'Late · promised' : 'Promised'} {dIso(b.backBy)}{b.eta && !houseUser && !hidePo ? <small> · usually all in {dIso(b.eta)}</small> : null}</span>}
            </div>
            <div className="od-bh-stats">
              <div><b>{b.quality}</b><small>Quality</small></div>
              <div><b>{active.length}</b><small>Fabrics</small></div>
              <div><b>{Math.round(req)} m</b><small>Total</small></div>
              {!hidePo && <div><b>{b.orders}</b><small>Orders</small></div>}
              <div className={pending.length ? 'pend' : 'done'}><b>{pending.length ? `${pendingM} m` : 'All in'}</b><small>{pending.length ? 'Pending' : 'Received'}</small></div>
            </div>
            {(() => {
              // batch journey: created → sent to printing (print files made) → sent to warehouse (part / full) → all received
              const ev = b.handover.events || [];
              const sends = ev.filter((e) => e.type === 'arrived').sort((x, y) => x.atMs - y.atMs);
              // when printing started: print files made in the app (a delivery date is not a printing date)
              const printMs = b.filesMs || b.handover.printingAtMs || null;
              const allIn = req > 0 && rec >= req - 1e-6;
              const partly = !allIn && (rec > 0 || sends.length > 0);
              const now = allIn ? 4 : partly ? 2 : (printMs || rec > 0) ? 2 : 1;
              const c0 = b.createdMs;
              const ago = (at) => plusDays(c0, at); // "+3 d" after creation
              const nowMs = Date.now();
              const firstSend = sends[0]?.atMs || null;
              const steps = [
                ['Created', null, c0 ? dt(c0) : null],
                [b.houseState === 'print_late' && !printMs ? 'Print pending' : 'Sent to printing', b.houseState === 'print_late' && !printMs ? 'bad' : null,
                  printMs ? `${dt(printMs)} · ${ago(printMs)}` : rec > 0 ? 'started' : now === 1 ? `${ago(nowMs)} so far · due ${dIso(b.printBy)}` : null, b.houseState === 'print_late' && !printMs ? '!' : undefined],
                [allIn ? 'Sent to warehouse' : partly ? 'Partly sent' : 'Sent to warehouse', b.houseState === 'rest_overdue' ? 'bad' : partly ? 'warn' : null,
                  partly && b.restDue ? `first part ${dt(b.firstPartMs || firstSend)} · ${ago(b.firstPartMs || firstSend)} · rest ${b.houseState === 'rest_overdue' ? 'was ' : ''}due ${dIso(b.restDue)}`
                    : firstSend ? `${dt(firstSend)} · ${ago(firstSend)}${sends.length > 1 ? ` · ${sends.length} deliveries` : ''}` : partly ? `${Math.round(rec * 10) / 10} of ${Math.round(req)} m in · ${ago(nowMs)} so far` : now === 2 ? `${ago(nowMs)} so far` : null,
                  b.houseState === 'rest_overdue' ? '!' : undefined],
                ['All received', null, allIn ? (b.receivedMs ? `${dt(b.receivedMs)} · ${ago(b.receivedMs)}` : 'done') : `${Math.round((req - rec) * 10) / 10} m pending`],
              ];
              return <Stepper steps={steps} now={now} label="Batch progress" />;
            })()}
            {!hidePo && <p className="od-bh-meta">
              {Math.round(rec * 10) / 10} of {Math.round(req)} m received{who ? ` · created by ${who}` : ''}
              {!hidePo && <> · <span className={b.po ? '' : 'od-late'}>{b.po ? `PO ${b.po}` : b.houseState === 'po_tonight' ? 'PO tonight 9 pm' : 'PO missing'}</span></>}
              {rec <= 0 && <> · {b.filesMs ? `print files made ${dt(b.filesMs)}` : 'print files not made in app yet'}</>}
            </p>}
          </header>
        );
      })();
  const handoverEl = b.status !== 'Received' && (() => {
        const hp = `/api/v1/order-desk/batches/${encodeURIComponent(b.id)}/handover`;
        const late = b.backBy && b.backBy < today;
        return (
          <section className={`od-verify ${arrived ? 'acc' : b.houseState === 'rest_overdue' || late ? 'bad' : ''}`} aria-label="Batch hand-over">
            <div className="od-verify-h"><b><ReportFlag section="batch" /> {arrived ? (b.handover.delivery ? `Delivery sent · ${b.handover.delivery.items.length} fabric${b.handover.delivery.items.length === 1 ? '' : 's'} · ${b.handover.delivery.metres} m` : 'Delivery with the warehouse') : 'With the production house'}</b>
              {arrived && <span className="od-small">sent {dt(b.handover.arrivedAtMs, true)} by {b.handover.arrivedBy}</span>}</div>
            {/* sent to printing = print files (TIFF) made; due by the end of the day after the batch was created */}
            {!arrived && rec <= 0 && !b.filesMs && !b.handover.printingAtMs && (
              <div className={`od-print-start${b.houseState === 'print_late' ? ' late' : ''}`}>
                <span>{b.houseState === 'print_late' ? `Not sent to printing yet: it was due by ${dIso(b.printBy)}. Make the print files (TIFF) to send it.` : `Send to printing by ${dIso(b.printBy)}: make the print files (TIFF).`}</span>
              </div>
            )}
            {!arrived && rec <= 0 && (b.filesMs || b.handover.printingAtMs) && <p className="od-small">Sent to printing {dt(b.filesMs || b.handover.printingAtMs, true)}</p>}
            {/* after a part, the rest must come within one cycle of this batch type, counted from the first part */}
            {!arrived && b.restDue && (
              <div className={`od-print-start${b.houseState === 'rest_overdue' ? ' late' : ''}`}>
                <span>{b.houseState === 'rest_overdue'
                  ? `Partial overdue: the remaining ${Math.round((req - rec) * 10) / 10} m was due by ${dIso(b.restDue)} (one ${b.quality || ''} cycle of ${b.cycleDays} working days after the first part on ${dt(b.firstPartMs)}).`
                  : `First part came ${dt(b.firstPartMs)}. Send the remaining ${Math.round((req - rec) * 10) / 10} m by ${dIso(b.restDue)} (one cycle of ${b.cycleDays} working days).`}</span>
              </div>
            )}
            {arrived ? (
              <div className="od-actions">
                <span className="od-small od-recv-hint">{b.handover.delivery ? 'Receive the fabrics in this delivery below; metres are filled in from what was sent.' : 'Receive each fabric below as it is unpacked.'}</span>
                {canRecv && <button className="od-btn" disabled={busy} onClick={() => act(hp, { action: 'delivery_done' }, rec >= req ? 'Batch complete' : 'Done: the missing lines go back to the office')}>Done for this delivery</button>}
                {canRecv && rec < req && (!fullAsk ? <button className="od-btn" disabled={busy} onClick={() => setFullAsk(true)}>Everything arrived in full</button>
                  : <span className="od-small">Receive all {Math.round(req - rec)} m remaining for every order? <button className="od-link" disabled={busy} onClick={receiveRest}>Yes, receive all</button><button className="od-link" onClick={() => setFullAsk(false)}>No</button></span>)}
                {(canRecv || canHouse) && <button className="od-btn" disabled={busy} onClick={() => act(hp, { action: 'undo_arrived' }, 'Moved back to the production house list')}>Not sent (undo)</button>}
                <a className="od-link" href={`/batches/${encodeURIComponent(b.id)}`}>Production page</a>
              </div>
            ) : (
              <>
                <div className="od-actions">
                  {(canHouse || canRecv) && !sending && <button className="od-btn od-pri" disabled={busy} onClick={() => setSending(true)}>Send delivery to warehouse…</button>}
                  {(canHouse || canRecv) && !sending && <span className="od-small od-arrive-hint">Before it leaves: tick the fabrics in this delivery (all or part). Anything not sent stays pending here.</span>}
                  {canHouse && !b.po && !hidePo && <button className="od-btn" disabled={busy} onClick={() => act(`/api/v9/production-batches/${encodeURIComponent(b.id)}/create-po`, {}, 'Purchase order created')}>Create PO now</button>}
                  {rec <= 0 && !b.filesMs && !BatchTools && <a className="od-btn" href={`/batches/${encodeURIComponent(b.id)}`}>Prepare print files</a>}
                </div>
                {sending && <SendDelivery pending={pending} busy={busy} onCancel={() => setSending(false)}
                  onSend={async (delivery) => { await act(hp, { action: 'arrived', delivery }, `Delivery sent: ${delivery.length} fabric${delivery.length === 1 ? '' : 's'} on the way to the warehouse`); setSending(false); }} />}
              </>
            )}
          </section>
        );
      })();
  // Messages about this batch: the production house, office and warehouse all read and write here
  const msgsEl = (() => {
        const msgs = (b.handover.events || []).filter((e) => (e.type === 'message' || e.type === 'chase') && e.note).slice(-5).reverse();
        const canWrite = canHouse || canRecv;
        if (!msgs.length && !canWrite) return null;
        return (
          <section className="od-msgs" aria-label="Messages about this batch">
            <div className="od-msgs-h"><b>Messages about this batch</b></div>
            {msgs.length > 0 && <ul className="od-msgs-list">{msgs.map((e) => <li key={e.atMs}><span>{e.note}</span><small>{e.by} · {dt(e.atMs, true)}</small></li>)}</ul>}
            {canWrite && (
              <div className="od-actions">
                <input id={`od-msg-${b.id}`} aria-label="Message about this batch" className="od-input od-msgs-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Pure part ready Friday" />
                <button className="od-btn" disabled={busy || !note.trim()} onClick={() => act(`/api/v1/order-desk/batches/${encodeURIComponent(b.id)}/handover`, { action: 'message', note }, 'Message sent')}>Send message</button>
              </div>
            )}
          </section>
        );
      })();
  // at the production house only what is still to come, with a small switch to see everything
  const pendingOnly = hidePo && !allFabrics && bRows.some((r) => r.kind === 'wait' || r.kind === 'act');
  const shownRows = pendingOnly ? bRows.filter((r) => r.kind === 'wait' || r.kind === 'act') : bRows;
  const fabricsEl = (
      <section className="od-sec">
        <div className="od-sec-h">
          <h3>{pendingOnly ? `Fabrics still to send (${shownRows.length} of ${b.items.length})` : `Fabrics in this batch (${b.items.length})`}</h3>
          {hidePo && bRows.some((r) => r.kind === 'wait' || r.kind === 'act') && bRows.some((r) => r.kind !== 'wait' && r.kind !== 'act') && (
            <button className="od-link od-sec-toggle" onClick={() => setAllFabrics((v) => !v)}>{allFabrics ? 'Show pending only' : `Show all ${b.items.length}`}</button>
          )}
        </div>
        {shownRows.map(({ i, kind, pill, expected }) => (
          <FabricRow key={i.sku} id={`od-brow-${b.id}-${i.sku}`} image={i.image} name={i.sku} hint={i.fabric || ''}
            sub={`${i.received > 0 && i.received < i.metres ? `${i.received} of ` : ''}${i.metres} m${i.fabric ? ` · ${i.fabric}` : ''} · ${i.orders.length} order${i.orders.length === 1 ? '' : 's'}`}
            pill={pill} kind={kind} open={bOpen.has(i.sku)} onToggle={() => setOpenB((prev) => { const n = new Set(prev || bOpen); if (n.has(i.sku)) n.delete(i.sku); else n.add(i.sku); return n; })}>
            <div className="od-bline-orders" role="list" aria-label={`Orders for ${i.sku}`}>
              <small>Orders ({i.orders.length})</small>
              {i.orders.map((x) => (
                <div key={x.lineItemId || x.number} className="od-bline-order" role="listitem">
                  {houseUser ? <span className="od-mono">#{x.number}</span>
                    : <button className="od-link od-mono" onClick={() => x.orderId && onOpenOrder(x.orderId)}>#{x.number}</button>}
                  {x.cancelled && arrived && <Tag tone="warn">order cancelled</Tag>}
                  <span className="od-bline-m">{x.metres} m{x.allocated >= x.metres - 1e-6 ? <span className="od-ok"> ✓ in</span> : x.allocated > 0 ? <span className="od-small"> · {x.allocated} in</span> : null}</span>
                </div>
              ))}
            </div>
            {kind === 'act' && canRecv && <ReceiveItem api={api} batchId={b.id} item={i} expected={expected} toast={toast} onDone={bump} />}
            {arrived && i.received < i.metres - 1e-6 && <PendingNote item={i} />}
            {kind === 'ok' && <ReceivedActions api={api} b={b} item={i} toast={toast} onDone={bump} isManager={isManager} />}
            {i.unavailable && <span className="od-small">Taken off this batch{i.releasedAtMs ? ` · ${dt(i.releasedAtMs)}` : ''}</span>}
          </FabricRow>
        ))}
      </section>
  );
  const historyList = b.handover.events.length > 0 && (
          <ol className="od-tl">{b.handover.events.slice().reverse().map((e) => <li key={e.atMs + e.type} className="done">{{ arrived: e.delivery ? `Delivery sent to warehouse · ${e.delivery.length} fabric${e.delivery.length === 1 ? '' : 's'} · ${e.deliveryMetres} m` : 'Delivery sent to warehouse', delivery_done: `Delivery received${e.pendingMetres ? ` · ${e.pendingMetres} m still pending` : ' · complete'}`, printing: 'Printing started', chase: `Message: ${e.note}`, message: `Message: ${e.note}`, undo_arrived: 'Arrived undone' }[e.type] || e.type}<small>{dt(e.atMs, true)} · {e.by}</small></li>)}</ol>
  );
  // At the production house (not yet sent): one clear next step, then what they need to know
  if (hidePo && b.status !== 'Received') {
    const printed = rec > 0 || !!b.filesMs || !!b.handover.printingAtMs;
    const leftM = Math.round((req - rec) * 10) / 10;
    const lateRest = b.houseState === 'rest_overdue';
    const latePrint = !printed && b.houseState === 'print_late';
    const backLate = printed && !b.restDue && b.backBy && b.backBy < today;
    const bad = lateRest || latePrint || backLate;
    const when = !printed
      ? (latePrint ? `Late: print files were due by ${dIso(b.printBy)}` : `Make the print files by ${dIso(b.printBy)}`)
      : b.restDue ? (lateRest ? `Partial overdue: the remaining ${leftM} m was due by ${dIso(b.restDue)}` : `Send the remaining ${leftM} m by ${dIso(b.restDue)} · first part came ${dt(b.firstPartMs)}`)
        : `Send all ${leftM} m by ${dIso(b.backBy)}${backLate ? ' (late)' : ''}`;
    const canSend = canHouse || canRecv;
    const sendBtn = canSend && !sending && <button className={`od-btn${printed ? ' od-pri' : ''}`} disabled={busy} onClick={() => setSending(true)}>Send delivery to warehouse…</button>;
    const tools = BatchTools && <BatchTools api={api} batchId={b.id} can={can} toast={toast} onChanged={bump} hideLabels inline primaryFiles={!printed} />;
    return (
      <div className="od-det od-house">
        <ReportArea api={api} toast={toast} kind="batch" refId={b.id} number={b.id} where={reportWhere}>
        {headerEl}
        <section className={`od-next${bad ? ' bad' : ''}`} aria-label="Next step">
          <div className="od-next-h"><span className="od-next-k">Next step <ReportFlag section="batch" /></span><b>{printed ? 'Send fabric to the warehouse' : 'Send this batch to printing'}</b></div>
          <p className="od-next-when">{when}</p>
          {printed && b.restDue && !lateRest && <p className="od-small">After a part delivery, the rest is due within one {b.quality} cycle ({b.cycleDays} working days) of the first part.</p>}
          {!sending && <div className="od-next-act">{printed ? <>{sendBtn}{tools}</> : <>{tools}{sendBtn}</>}</div>}
          {sending && <SendDelivery pending={pending} busy={busy} onCancel={() => setSending(false)}
            onSend={async (delivery) => { await act(`/api/v1/order-desk/batches/${encodeURIComponent(b.id)}/handover`, { action: 'arrived', delivery }, `Delivery sent: ${delivery.length} fabric${delivery.length === 1 ? '' : 's'} on the way to the warehouse`); setSending(false); }} />}
        </section>
        {msgsEl}
        <FabricNeed items={active} title="Fabric needed" leftLabel="Still to send" />
        {fabricsEl}
        {historyList && <details className="od-hist"><summary>History ({b.handover.events.length})</summary>{historyList}</details>}
        </ReportArea>
      </div>
    );
  }
  return (
    <div className="od-det">
      <ReportArea api={api} toast={toast} kind="batch" refId={b.id} number={b.id} where={reportWhere}>
      {headerEl}
      {handoverEl}
      {msgsEl}
      {BatchTools && <BatchTools api={api} batchId={b.id} can={can} toast={toast} onChanged={bump} hideLabels={hidePo} />}
      <FabricNeed items={active} />
      {!houseUser && <PrintRunsBox api={api} b={b} toast={toast} onDone={bump} />}
      {fabricsEl}
      {historyList && <section className="od-card"><h3>Chase and hand-over history</h3>{historyList}</section>}
      <Links links={[{ label: 'Open in Production', href: `/batches/${encodeURIComponent(b.id)}` }, { label: 'All batches', href: '/batches' }]} />
      </ReportArea>
    </div>
  );
}

// One fabric needed by several orders: what it is, where it sits, and the shared piece plan
function FabricGroupHeader({ rows }) {
  const first = rows[0];
  const metres = Math.round(rows.reduce((t, r) => t + r.metres, 0) * 10) / 10;
  const pieces = new Map();
  rows.forEach((r) => (r.plan || []).forEach((x) => { const p = pieces.get(x.pieceId) || { id: x.pieceId, len: x.pieceLength, location: x.location, takes: [] }; p.len = Math.max(p.len, x.pieceLength); p.takes.push(`#${r.number} ${x.qty} m`); pieces.set(x.pieceId, p); }));
  const left = new Map((first.groupPieces || []).map((p) => [p.id, p.left]));
  const short = rows.filter((r) => r.short);
  const rack = [...pieces.values()].map((p) => p.location).filter(Boolean)[0];
  return (
    <div className="od-group">
      <div className="od-group-h">
        {first.image ? <img src={first.image} alt="" loading="lazy" /> : <span className="od-noimg" />}
        <span className="od-group-t" title={first.name || ''}><b className="od-mono">{first.sku}</b><small>{rack ? `rack ${rack}` : 'no rack yet'}</small></span>
        <span className="od-small">{rows.length} orders · {metres} m</span>
      </div>
      <div className="od-group-plan">
        <small>PIECE PLAN</small>
        {[...pieces.values()].map((p) => <span key={p.id}><b className="od-mono">{p.id}</b> <small>({p.len} m)</small> → {p.takes.join(' + ')} <small>· {left.get(p.id) ?? 0} m left</small></span>)}
        {short.length > 0 && <span className="od-late">{short.map((r) => `#${r.number}`).join(', ')}: no piece covers it, so it goes to the next batch</span>}
      </div>
    </div>
  );
}

// drag the edge of a column to change its width (saved per person); double-click puts it back
const COL_DEFAULT = { '--od-list-w': 340, '--od-rail-w': 300 };
const COL_LIMIT = { '--od-list-w': [240, 560], '--od-rail-w': [240, 480] };
function ColResizer({ varName, grow = 1, label }) {
  const start = (e) => {
    e.preventDefault();
    const page = e.currentTarget.closest('.od-page');
    if (!page) return;
    const x0 = Number(e.clientX);
    if (!Number.isFinite(x0)) return;
    const w0 = parseFloat(getComputedStyle(page).getPropertyValue(varName)) || COL_DEFAULT[varName];
    const [lo, hi] = COL_LIMIT[varName];
    let w = w0;
    const move = (ev) => { const dx = Number(ev.clientX) - x0; if (!Number.isFinite(dx)) return; w = Math.min(hi, Math.max(lo, w0 + grow * dx)); page.style.setProperty(varName, `${w}px`); };
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      try { localStorage.setItem(`od-col${varName}`, String(Math.round(w))); } catch { /* storage unavailable */ }
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const reset = (e) => {
    const page = e.currentTarget.closest('.od-page');
    page?.style.setProperty(varName, `${COL_DEFAULT[varName]}px`);
    try { localStorage.removeItem(`od-col${varName}`); } catch { /* storage unavailable */ }
  };
  return <div className={`od-resizer${grow < 0 ? ' left' : ''}`} role="separator" aria-orientation="vertical" aria-label={label} title="Drag to resize · double-click to reset" onPointerDown={start} onDoubleClick={reset} />;
}
const savedCols = () => {
  const out = {};
  Object.keys(COL_DEFAULT).forEach((k) => { try { const v = Number(localStorage.getItem(`od-col${k}`)); if (v) out[k] = `${v}px`; } catch { /* storage unavailable */ } });
  return out;
};

function OrderDesk({ api, team = null, authedFetch = null, isManager = false, access = null, batchTools = null }) {
  const can = useCallback((k) => (access ? !!access.can?.[k] : true), [access]);
  const tabOn = useCallback((id) => (access ? (access.tabs?.[id] || 'off') !== 'off' : true), [access]);
  // last lists seen in this tab: shown at once on a page refresh while fresh ones load
  const [data, setData] = useState(() => { try { return JSON.parse(sessionStorage.getItem('od-queues') || 'null'); } catch { return null; } });
  const [error, setError] = useState(null);
  const [qid, setQid] = useState(() => { try { return localStorage.getItem('od-queue') || (team === 'warehouse' ? 'today' : 'verify'); } catch { return team === 'warehouse' ? 'today' : 'verify'; } });
  const [openBatch, setOpenBatch] = useState(null);
  const [showOverview, setShowOverview] = useState(false);
  const [allMode, setAllMode] = useState(false); // "All orders": every order, newest first, a page at a time
  const [bulk, setBulk] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [scan, setScan] = useState('');
  const [scanning, setScanning] = useState(false);
  const [sel, setSel] = useState(null); // { kind, id }
  const [openOrder, setOpenOrder] = useState(null); // order opened from a group/batch
  const [search, setSearch] = useState('');
  const [stageTab, setStageTab] = useState('all'); // batch lists: filter by stage (Not started, Printing late, …)
  const [msg, setMsg] = useState(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [focus, setFocus] = useState(false); // one item at a time, list hidden
  const [muView, setMuView] = useState(() => { try { return localStorage.getItem('od-mu-view') || 'order'; } catch { return 'order'; } });
  const [doneCount, setDoneCount] = useState(0);
  const scanRef = useRef(null);
  const listRef = useRef(null);
  const selIdx = useRef(0);
  const toast = useCallback((text, isErr) => { setMsg({ text, isErr }); setTimeout(() => setMsg(null), 3500); }, []);

  const loadSeq = useRef(0);
  const load = useCallback(async (refresh) => {
    const seq = ++loadSeq.current;
    try {
      const r = await api(`/api/v1/order-desk/queues${refresh ? '?refresh=1' : ''}`);
      if (seq !== loadSeq.current) return r; // a newer load already started
      setData(r);
      setError(null);
      try { sessionStorage.setItem('od-queues', JSON.stringify(r)); } catch { /* full or unavailable */ }
      return r;
    } catch (e) { if (seq === loadSeq.current) setError(e.message); return null; }
  }, [api]);
  // a finished cut / restock / receive / pack leaves its list at once; the full lists refresh behind it
  const dropRow = useCallback((row) => {
    if (!row) return;
    setData((d) => (d ? { ...d, queues: d.queues.map((x) => { const rows = x.rows.filter((y) => !(y.kind === row.kind && y.id === row.id)); return rows.length === x.rows.length ? x : { ...x, rows, count: Math.max(0, (x.count || 0) - (x.rows.length - rows.length)) }; }) } : d));
  }, []);
  useEffect(() => {
    // ready-made lists come back in well under a second; if they are older than a minute, rebuild quietly behind them
    load().then((r) => { if (r?.builtAtMs && Date.now() - r.builtAtMs > 60e3) load(true); });
    // every 30 s ask "did any order change?" (cheap); reload the lists only when it did. Full reload every 5 min anyway.
    let builtAt = 0;
    const check = async () => {
      if (document.hidden) return;
      try {
        const v = await api('/api/v1/order-desk/queues/version');
        if (v?.dirtyAtMs && v.dirtyAtMs > builtAt) { const r = await load(); if (r?.builtAtMs) builtAt = r.builtAtMs; } // older than the change: asked again next time
      } catch { /* next check */ }
    };
    const t = setInterval(check, 30e3);
    const full = setInterval(() => { if (!document.hidden) load().then((r) => { if (r?.builtAtMs) builtAt = Math.max(builtAt, r.builtAtMs); }); }, 300e3);
    return () => { clearInterval(t); clearInterval(full); };
  }, [load, api]);

  const queues = useMemo(() => (data?.queues || []).filter((q) => (access ? tabOn(q.id) : (!team || q.team === team || q.team === 'all'))), [data, team, access, tabOn]);
  const q = queues.find((x) => x.id === qid) || queues[0];
  const byFabric = q?.id === 'markused' && muView === 'fabric';
  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!q) return [];
    const staged = stageTab === 'all' ? q.rows : q.rows.filter((r) => r.stage === stageTab);
    const found = !s ? staged : staged.filter((r) => `${r.number} ${r.customer || ''} ${r.city || ''} ${r.sku || ''} ${r.name || ''} ${r.chips.join(' ')} ${r.group ? r.group.lines.map((l) => `${l.number} ${l.sku}`).join(' ') : ''}`.toLowerCase().includes(s));
    if (!byFabric) return found;
    // fabrics needed by 2+ orders first (most urgent group first, urgent order first inside), then the single lines
    const groups = new Map();
    found.forEach((r) => { const k = r.groupKey || r.id; if (!groups.has(k)) groups.set(k, []); groups.get(k).push(r); });
    const multi = [...groups.values()].filter((g) => g.length > 1).map((g) => [...g].sort((a, b) => a.sortMs - b.sortMs)).sort((a, b) => a[0].sortMs - b[0].sortMs);
    return [...multi.flat(), ...found.filter((r) => groups.get(r.groupKey || r.id).length === 1)];
  }, [q, search, byFabric, stageTab]);
  const fabricGroups = useMemo(() => {
    const m = new Map();
    (q?.id === 'markused' ? rows : []).forEach((r) => { const k = r.groupKey || r.id; if (!m.has(k)) m.set(k, []); m.get(k).push(r); });
    return m;
  }, [q, rows]);
  const sharedFabrics = useMemo(() => {
    if (q?.id !== 'markused') return 0;
    const c = {}; q.rows.forEach((r) => { c[r.groupKey || r.id] = (c[r.groupKey || r.id] || 0) + 1; });
    return Object.values(c).filter((n) => n > 1).length;
  }, [q]);
  const pickMuView = (v) => { setMuView(v); try { localStorage.setItem('od-mu-view', v); } catch { /* storage unavailable */ } };
  const same = (r) => sel && r.id === sel.id && r.kind === sel.kind;
  // Keep a selection; when the selected row leaves the list after an action, open the one that took its place (auto-next)
  useEffect(() => {
    if (!q || openOrder || openBatch || showOverview) return;
    const i = rows.findIndex(same);
    if (i >= 0) { selIdx.current = i; return; }
    if (!rows.length) { if (sel) setSel(null); return; }
    const next = rows[Math.min(selIdx.current, rows.length - 1)];
    if (sel) setDoneCount((n) => n + 1);
    setSel({ kind: next.kind, id: next.id });
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const i = rows.findIndex(same); if (i >= 0) selIdx.current = i; }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickQueue = (id) => {
    setQid(id); setAllMode(false); setSearch(''); setStageTab('all'); setOpenOrder(null); setOpenBatch(null); setShowOverview(false); setBulk(new Set()); setMobileDetail(false); setDoneCount(0);
    try { localStorage.setItem('od-queue', id); } catch { /* storage unavailable */ }
    const first = (data?.queues || []).find((x) => x.id === id)?.rows[0];
    selIdx.current = 0;
    setSel(first ? { kind: first.kind, id: first.id } : null);
  };
  const pickRow = (r) => { setSel({ kind: r.kind, id: r.id }); setOpenOrder(null); setOpenBatch(null); setShowOverview(false); setMobileDetail(true); };
  const step = useCallback((d) => {
    if (!rows.length) return;
    const i = Math.max(0, rows.findIndex(same));
    const r = rows[Math.min(rows.length - 1, Math.max(0, i + d))];
    setSel({ kind: r.kind, id: r.id }); setOpenOrder(null); setOpenBatch(null); setShowOverview(false);
    requestAnimationFrame(() => listRef.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'nearest' }));
  }, [rows, sel]); // eslint-disable-line react-hooks/exhaustive-deps
  // Keyboard: / scan box, J / K next and previous, Esc leaves focus mode
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
      if (document.querySelector('[aria-modal="true"], .mu-modal-overlay, .rpm-overlay')) return;
      if (e.key === '/') { e.preventDefault(); scanRef.current?.focus(); }
      else if (e.key === 'j' || e.key === 'J') { e.preventDefault(); step(1); }
      else if (e.key === 'k' || e.key === 'K') { e.preventDefault(); step(-1); }
      else if (e.key === 'Escape' && focus) setFocus(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, focus]);

  // Stuck orders: fix many at once (same routes as the single-order buttons, one by one)
  const runBulk = async (kind) => {
    const rowsById = new Map((q?.rows || []).map((r) => [r.id, r]));
    setBulkBusy(true);
    let ok = 0; const fails = [];
    for (const id of bulk) {
      const r = rowsById.get(id);
      const awb = r?.awb || `HAND-${r?.number || id}`;
      try { await api(`/api/v9/orders/${encodeURIComponent(id)}/manual-deliver`, 'POST', { awb: kind === 'hand' ? (r?.awb || `HAND-${r?.number || id}`) : awb }); ok += 1; } catch (e) { fails.push(`#${r?.number || id}: ${e.message}`); }
    }
    setBulkBusy(false);
    setBulk(new Set());
    toast(`${ok} marked delivered${fails.length ? ` · ${fails.length} failed (${fails.slice(0, 2).join('; ')})` : ''}`, fails.length > 0);
    load(true);
  };
  const doScan = async (e) => {
    e.preventDefault();
    if (!scan.trim()) return;
    setScanning(true);
    try {
      const r = await api(`/api/v1/order-desk/find?q=${encodeURIComponent(scan.trim())}`);
      if (r.kind === 'batch') { setOpenBatch(r.id); setOpenOrder(null); } else { setOpenOrder(r.id); setOpenBatch(null); }
      setShowOverview(false);
      setMobileDetail(true);
      setScan('');
    } catch (err) { toast(err.message, true); } finally { setScanning(false); }
  };
  const selRow = q?.rows.find(same);
  // only drop the selected row when the finished action was for that row's own work (its line, or the order in Pack)
  const changedFromSel = (x) => {
    if (x?.done && selRow && (selRow.kind === 'line' || q?.id === 'pack')) dropRow(selRow);
    load(true);
  };

  if (error && !data) return <div className="od-page"><div className="od-empty"><b>Couldn't load Order Desk</b>{error}<button className="od-btn" onClick={() => load(true)}>Try again</button></div></div>;
  if (!data) return <div className="od-page"><div className="od-skel" style={{ height: 480 }} aria-busy="true" /></div>;

  const pos = Math.max(0, rows.findIndex(same));
  const payChip = (r) => r.pay && <span className={`od-pay${r.pay === 'COD' ? ' cod' : ''}`}>{r.pay}{r.pay === 'COD' && r.amount ? ` ${inr(r.amount)}` : ''}</span>;
  // production-house staff don't see our "usually all in" estimate (office and warehouse do)
  const chipsOf = (r) => r.chips.filter((c) => !/^COD( ₹|$)/.test(c) && !(access?.role === 'production' && /^usually all in/.test(c)));
  let lastTeam = null;
  return (
    <DeskCtx.Provider value={{ isManager: access ? access.role === 'manager' : isManager, can, BatchTools: batchTools, role: access?.role || null, where: { queue: q?.id || null, row: !openOrder && !openBatch ? selRow || null : null } }}>
    <div className={`od-page${mobileDetail ? ' od-m-detail' : ''}${focus ? ' od-focus' : ''}`} style={savedCols()}>
      <div className="od-bar">
        <div className="od-bar-q">
          <b>{showOverview === 'review' ? 'Review list' : showOverview === 'reports' ? 'Reports' : showOverview ? 'Overview' : allMode ? 'All orders' : q?.label}{focus ? ' · focus' : ''}</b>
          {!showOverview && allMode && <span className="od-small">Every order, newest first. Open one to check it.</span>}
          {!showOverview && !allMode && q?.note && <span className="od-small">{q.count} · {q.note}</span>}
        </div>
        <form className="od-scan" onSubmit={doScan} role="search">
          <span className="od-scan-box">
            <input ref={scanRef} className="od-input" value={scan} onChange={(e) => setScan(e.target.value)} placeholder="Scan or type order no., AWB or batch" aria-label="Scan or type order number, AWB or batch" />
            <kbd className="od-kbd">/</kbd>
          </span>
          <button className="od-btn" type="submit" disabled={scanning}>{scanning ? 'Finding…' : 'Open'}</button>
          {!showOverview && rows.length > 0 && <button className={`od-btn od-focus-btn${focus ? ' od-pri' : ''}`} type="button" onClick={() => setFocus((f) => !f)}>{focus ? 'Exit focus' : 'Focus'}</button>}
          <button className="od-btn" type="button" onClick={() => load(true)} title={`Updated ${dt(data.nowMs, true)}`}>Refresh</button>
        </form>
      </div>
      {focus && (
        <div className="od-strip" aria-label="Focus progress">
          <span className="od-small">{doneCount} done · {rows.length} left</span>
          <div className="od-prog"><i style={{ width: `${Math.round((doneCount / Math.max(1, doneCount + rows.length)) * 100)}%` }} /></div>
          <div className="od-strip-rows">
            {rows.slice(Math.max(0, pos - 3), pos + 12).map((r) => (
              <button key={`${r.kind}-${r.id}`} aria-current={same(r)} onClick={() => pickRow(r)} className="od-mono">{r.kind === 'order' || r.kind === 'line' ? `#${r.number}` : r.number}</button>
            ))}
          </div>
        </div>
      )}
      <ReportUpdates api={api} onOpen={(u) => { setShowOverview(false); if (u.kind === 'batch') { setOpenBatch(u.refId); setOpenOrder(null); } else { setOpenOrder(u.refId); setOpenBatch(null); } setMobileDetail(true); }} />
      <div className="od-desk">
        <nav className="od-q" aria-label="Queues">
          {(access ? tabOn('overview') : !team) && <button aria-current={showOverview === 'overview'} onClick={() => { setShowOverview('overview'); setAllMode(false); setMobileDetail(true); }}><span>Overview</span><span className="od-c">·</span></button>}
          {tabOn('allorders') && <button aria-current={allMode && !showOverview} onClick={() => { setAllMode(true); setShowOverview(false); setOpenOrder(null); setOpenBatch(null); setSel(null); setMobileDetail(false); }}><span>All orders</span><span className="od-c">·</span></button>}
          {tabOn('flowreview') && <button aria-current={showOverview === 'review'} onClick={() => { setShowOverview('review'); setAllMode(false); setOpenOrder(null); setOpenBatch(null); setMobileDetail(true); }}><span>Review list</span><span className="od-c">·</span></button>}
          {can('reports.manage') && <button aria-current={showOverview === 'reports'} onClick={() => { setShowOverview('reports'); setAllMode(false); setOpenOrder(null); setOpenBatch(null); setMobileDetail(true); }}><span>Reports</span><span className="od-c">·</span></button>}
          {/* empty tabs are hidden; the one you are on stays until you leave it */}
          {queues.filter((x) => x.count > 0 || (!showOverview && x.id === q?.id)).map((x) => {
            const head = x.team !== lastTeam ? <h3 key={`h-${x.team}`} className={`od-qh ${x.team}`}><span className="od-qh-mark" aria-hidden="true" />{TEAM_LABEL[x.team]}</h3> : null;
            lastTeam = x.team;
            return (
              <React.Fragment key={x.id}>
                {head}
                <button aria-current={!showOverview && !allMode && x.id === q?.id} className={x.count ? '' : 'od-zero'} onClick={() => pickQueue(x.id)}>
                  <span>{x.label}</span><span className={`od-c${x.late ? ' hot' : ''}`}>{x.count}</span>
                </button>
              </React.Fragment>
            );
          })}
        </nav>
        <div className="od-list" aria-label={allMode ? 'All orders' : q?.label} ref={listRef}>
          <ColResizer varName="--od-list-w" grow={1} label="Resize the list" />
          {allMode ? <AllOrdersList api={api} current={openOrder} onOpen={(id) => { setOpenOrder(id); setOpenBatch(null); setShowOverview(false); setMobileDetail(true); }} /> : <>
          <div className="od-list-h">
            <input className="od-input" placeholder="Search order, customer, city, SKU…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search this queue" />
            {q && q.rows.some((r) => r.stage) && (() => {
              // status tabs for batch lists: the stages present, most urgent first
              const ORDER = ['Partial overdue', 'Print pending', 'Not started', 'Printing', 'Partial', 'All in'];
              const counts = new Map();
              q.rows.forEach((r) => { if (r.stage) counts.set(r.stage, (counts.get(r.stage) || 0) + 1); });
              const stages = [...counts.keys()].sort((a, b) => (ORDER.indexOf(a) + 99) % 99 - (ORDER.indexOf(b) + 99) % 99);
              return (
                <div className="od-stage-tabs" role="tablist" aria-label="Batch status">
                  <button role="tab" aria-selected={stageTab === 'all'} onClick={() => setStageTab('all')}>All <span>{q.rows.length}</span></button>
                  {stages.map((st) => <button key={st} role="tab" aria-selected={stageTab === st} className={st === 'Print pending' || st === 'Partial overdue' ? 'bad' : ''} onClick={() => setStageTab(st)}>{st} <span>{counts.get(st)}</span></button>)}
                </div>
              );
            })()}
            {q?.id === 'markused' && (
              <div className="od-seg" role="group" aria-label="Mark used view">
                <button aria-pressed={!byFabric} onClick={() => pickMuView('order')}>By order</button>
                <button aria-pressed={byFabric} onClick={() => pickMuView('fabric')}>By fabric{sharedFabrics ? ` (${sharedFabrics})` : ''}</button>
              </div>
            )}
            <span className="od-small od-keys"><kbd className="od-kbd">J</kbd><kbd className="od-kbd">K</kbd> next / previous</span>
          </div>
          {rows.length === 0 && <div className="od-empty">{search ? 'Nothing matches.' : 'Nothing here right now.'}</div>}
          {q?.id === 'stuck' && rows.length > 0 && can('courier.fix') && (
            <div className="od-bulk">
              <label className="od-small"><input type="checkbox" checked={bulk.size > 0 && bulk.size === rows.length} onChange={(e) => setBulk(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} /> Select all ({rows.length})</label>
              {bulk.size > 0 && <><button className="od-btn od-pri" disabled={bulkBusy} onClick={() => runBulk('delivered')}>{bulkBusy ? 'Working…' : `Mark ${bulk.size} delivered`}</button><span className="od-small">Orders without an AWB are recorded as hand-delivered.</span></>}
            </div>
          )}
          {byFabric && rows.length > 0 && sharedFabrics === 0 && <div className="od-small od-group-none">No fabric is needed by more than one order right now.</div>}
          {rows.map((r, ri) => (r.kind === 'line' ? (
            <React.Fragment key={`line-${r.id}`}>
            {byFabric && (fabricGroups.get(r.groupKey || r.id) || []).length > 1 && (ri === 0 || (rows[ri - 1].groupKey || rows[ri - 1].id) !== (r.groupKey || r.id)) && <FabricGroupHeader rows={fabricGroups.get(r.groupKey || r.id)} />}
            {byFabric && ri > 0 && (fabricGroups.get(r.groupKey || r.id) || []).length === 1 && (fabricGroups.get(rows[ri - 1].groupKey || rows[ri - 1].id) || []).length > 1 && <div className="od-group-sep">Single fabrics</div>}
            <div className={`od-row od-line${same(r) ? ' on' : ''}${byFabric && (fabricGroups.get(r.groupKey || r.id) || []).length > 1 ? ' in-group' : ''}`}>
              <button className="od-line-main" onClick={() => pickRow(r)} aria-current={same(r)}>
                {r.image ? <img src={r.image} alt="" loading="lazy" /> : <span className="od-noimg" />}
                <span className="od-line-text" title={r.name || ''}>
                  <b className="od-mono od-line-sku">{r.sku}</b>
                  <span className="od-line-sub"><span className="od-mono">#{r.number} · {r.metres} m{r.piece?.location ? ` · rack ${r.piece.location}` : ''}</span>
                    {r.restock ? chipsOf(r).slice(0, 1).map((c) => <Tag key={c} tone={/RTO/.test(c) ? 'warn' : 'plain'}>{/never shipped/i.test(c) ? 'Extra · never shipped' : c}</Tag>)
                      : <span className={`od-due ${r.dueCls}`}>{r.due}</span>}
                    {r.pay === 'COD' && payChip(r)}</span>
                </span>
              </button>
              <div className="od-line-act">
                {r.restock ? can('restock.do') && <RestockButton api={api} orderId={r.orderId} orderNumber={r.number} item={r} label={`Restock ${r.metres} m`} toast={toast} onDone={() => { dropRow(r); load(true); }} />
                  : can('markused.do') && <MarkUsedButton api={api} orderId={r.orderId} orderNumber={r.number} item={{ ...r, pending: r.metres }} plan={r.plan} label={`Mark used ${r.metres} m`} toast={toast} onDone={() => { dropRow(r); load(true); }} />}
              </div>
            </div>
            </React.Fragment>
          ) : (
            <div key={`${r.kind}-${r.id}`} className={q?.id === 'stuck' ? 'od-row-wrap' : undefined}>
            {q?.id === 'stuck' && can('courier.fix') && <input type="checkbox" className="od-row-check" aria-label={`Select #${r.number}`} checked={bulk.has(r.id)} onChange={() => setBulk((p) => { const n = new Set(p); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; })} />}
            <button className={`od-row${r.stage === 'Print pending' || r.stage === 'Partial overdue' ? ' od-row-alert' : ''}${r.kind === 'batch' && r.batch ? ' od-row-batch' : ''}`} aria-current={same(r)} onClick={() => pickRow(r)}>
              {r.kind === 'batch' && r.batch ? (
                // batch row: name + late, the journey, then one plain line (created · fabrics · metres) and any message
                <>
                  <span className="od-row-top"><b className="od-mono">{r.number}</b>{r.dueCls === 'late' && <span className="od-due late">{r.due}</span>}</span>
                  {r.batch.metres != null && <MiniJourney b={r.batch} stage={r.stage} />}
                  <span className="od-batch-meta">{[r.batch.createdMs ? `Created ${dt(r.batch.createdMs)}` : null, r.side].filter(Boolean).join(' · ')}</span>
                  {chipsOf(r).length > 0 && <span className="od-batch-note">{chipsOf(r).join(' · ')}</span>}
                </>
              ) : (<>
              <span className="od-row-top"><b className="od-mono">{r.kind === 'order' ? `#${r.number || r.id}` : r.number}</b>{r.stage && <span className={`od-row-stage ${r.stage === 'Partial' ? 'warn' : r.stage === 'Print pending' || r.stage === 'Partial overdue' ? 'bad' : r.stage === 'Printing' ? 'acc' : ''}`}>{r.stage}</span>}{(r.kind !== 'batch' || r.dueCls === 'late') && <span className={`od-due ${r.dueCls}`}>{r.due}</span>}</span>
              {(r.fabrics || r.city || r.pay) && <span className="od-row-sub"><span>{[r.fabrics, titleCase(r.city)].filter(Boolean).join(' · ')}</span>{payChip(r)}</span>}
              {(chipsOf(r).length > 0 || r.side || (r.kind === 'batch' && r.batch?.createdMs)) && <span className="od-chips">{r.kind === 'batch' && r.batch?.createdMs && <Tag>Created {dt(r.batch.createdMs)}</Tag>}{chipsOf(r).map((c) => <Tag key={c} tone={/late|miss|LOST|check|No |never|failed|not updated/.test(c) ? 'warn' : 'plain'}>{c}</Tag>)}{r.side && <span className="od-chip-side">{r.side}</span>}</span>}
              </>)}
            </button>
            </div>
          )))}
          </>}
        </div>
        <div className="od-detail">
          <button className="od-back" onClick={() => (openOrder ? setOpenOrder(null) : openBatch ? setOpenBatch(null) : setMobileDetail(false))}>← Back</button>
          {showOverview === 'review' ? <FlowReview api={api} toast={toast} canAct={can('review.act')} onOpenOrder={(id) => { setShowOverview(false); setOpenOrder(id); setOpenBatch(null); setMobileDetail(true); }} />
            : showOverview === 'reports' ? <ReportsView api={api} toast={toast} onOpen={(r) => { setShowOverview(false); if (r.kind === 'batch') { setOpenBatch(r.refId); setOpenOrder(null); } else { setOpenOrder(r.refId); setOpenBatch(null); } }} />
            : showOverview ? <Overview api={api} />
            : openBatch ? <BatchView authedFetch={authedFetch} api={api} batchId={openBatch} onOpenOrder={(id) => { setOpenBatch(null); setOpenOrder(id); }} toast={toast} onChanged={() => load(true)} />
            : openOrder ? <OrderView api={api} orderId={openOrder} toast={toast} onOpenOrder={setOpenOrder} onChanged={() => load(true)} authedFetch={authedFetch} />
            : allMode ? <div className="od-empty">Pick an order from the list to open it.</div>
            : !selRow ? <div className="od-empty">{rows.length ? 'Pick something from the list.' : 'All done in this queue.'}</div>
              : selRow.kind === 'line' ? <OrderView key={selRow.id} api={api} orderId={selRow.orderId} line={selRow} toast={toast} onOpenOrder={setOpenOrder} onChanged={changedFromSel} authedFetch={authedFetch} />
              : selRow.kind === 'order' ? <OrderView api={api} orderId={selRow.id} toast={toast} onOpenOrder={setOpenOrder} onChanged={changedFromSel} authedFetch={authedFetch} />
                : selRow.kind === 'run' ? <RunView key={selRow.id} api={api} row={selRow} toast={toast} onChanged={() => load(true)} onOpenBatch={(id) => { setOpenBatch(id); setOpenOrder(null); }} />
                : selRow.kind === 'group' ? <GroupView api={api} row={selRow} onOpenOrder={setOpenOrder} toast={toast} onChanged={() => load(true)} batchDays={data.settings?.batchDays} />
                  : <BatchView authedFetch={authedFetch} api={api} batchId={selRow.id} houseView={q?.id === 'house'} onOpenOrder={setOpenOrder} toast={toast} onChanged={() => load(true)} />}
        </div>
      </div>
      {focus && (
        <div className="od-dock">
          <button className="od-btn" onClick={() => step(-1)} disabled={pos === 0}>← Previous <kbd className="od-kbd">K</kbd></button>
          <span className="od-small">{rows.length ? `${pos + 1} of ${rows.length}` : 'Queue empty'} · the next one opens by itself after each action</span>
          <button className="od-btn" onClick={() => step(1)} disabled={pos >= rows.length - 1}>Skip <kbd className="od-kbd">J</kbd> →</button>
        </div>
      )}
      {msg && <div className={`od-toast${msg.isErr ? ' err' : ''}`} role="status">{msg.text}</div>}
    </div>
    </DeskCtx.Provider>
  );
}

export default OrderDesk;
